import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  GitBranch, Play, Save, Trash2, LayoutGrid,
  X, Search, Loader2, CheckCircle2, Clock, Calendar, AlertTriangle, Bot,
  Zap, Timer, Send, MessageSquare, GitMerge, Hourglass, Link2,
  Mail, FileText, Hash, Globe, BarChart2, PenLine,
  Lightbulb, DollarSign, CalendarDays, Bookmark, BookOpen, Settings2,
  ChevronDown, ChevronUp, Smartphone,
} from "lucide-react";
import { useAgents } from "../../context/AgentsContext";
import { useAI } from "../../context/AIContext";
import { flowScheduleAPI, customAgentAPI, externalAgentsAPI, gmailAPI, API_ORIGIN } from "../../services/api";
import { useDomainAgentSelection } from "../../hooks/useDomainAgentSelection.ts";
import { alpha } from '../../utils/alpha';

// ─── Theme ────────────────────────────────────────────────────────────────────
const T = {
  page:        'var(--lyra-color-surface-2)',
  surface:     'var(--lyra-color-surface)',
  surface2:    'var(--lyra-color-surface-2)',
  surface3:    'var(--app-surface-3)',
  border:      'var(--lyra-color-border)',
  border2:     'var(--app-border-strong)',
  ink:         'var(--lyra-color-text)',
  ink2:        '#4A4A47',
  ink3:        'var(--app-text-2)',
  ink4:        'var(--app-text-3)',
  accent:      'var(--app-primary-mid)',
  accentLight: 'var(--app-primary-soft)',
  accentMid:   'var(--app-primary-soft-2)',
  accentDark:  'var(--app-primary-deep)',
  green:       'var(--lyra-color-success)',
  greenBg:     'var(--lyra-color-success-bg)',
  greenBd:     'var(--app-success-border)',
  red:         'var(--lyra-color-danger)',
  redBg:       'var(--lyra-color-danger-bg)',
  redBd:       'var(--app-danger-border)',
  amber:       'var(--lyra-color-warning)',
  amberBg:     'var(--lyra-color-warning-bg)',
  amberBd:     'var(--app-warning-border)',
  violet:      'var(--lyra-color-primary)',
  violetBg:    'var(--app-primary-soft)',
  violetBd:    'var(--app-primary-soft-2)',
  bg:      'var(--lyra-color-surface-2)',
  white:   'var(--lyra-color-surface)',
  text:    'var(--lyra-color-text)',
  muted:   'var(--app-text-2)',
  dim:     'var(--app-text-3)',
  accentBg: 'var(--app-primary-soft)',
  accentBd: 'var(--app-primary-soft-2)',
  purple:   'var(--lyra-color-primary)',
  purpleBg: 'var(--app-primary-soft)',
  purpleBd: 'var(--app-primary-soft-2)',
};

// ─── Node type colour system ──────────────────────────────────────────────────
const NODE_TYPES = {
  trigger: { bg: 'var(--lyra-color-warning-bg)', border: 'var(--app-warning-border)', text: 'var(--lyra-color-warning)', tag: 'Trigger'  },
  agent:   { bg: 'var(--app-primary-soft)', border: '#5DCAA5', text: 'var(--lyra-color-success)', tag: 'AI agent' },
  logic:   { bg: 'var(--app-primary-soft)', border: '#AFA9EC', text: 'var(--lyra-color-primary)', tag: 'Logic'    },
  output:  { bg: 'var(--app-primary-soft)', border: '#85B7EB', text: 'var(--lyra-color-primary)', tag: 'Output'   },
};

const TRIGGER_IDS = new Set(["webhook", "schedule", "telegram", "whatsapp", "mobile-trigger"]);
const LOGIC_IDS   = new Set(["condition", "delay", "merge"]);
const OUTPUT_IDS  = new Set(["gmail", "notion", "slackout", "webhkout", "telegramSend"]);
const GMAIL_TRIGGER_RULES = [
  { id: "subject", label: "Subject contains", placeholder: "e.g. task" },
  { id: "from", label: "From address contains", placeholder: "e.g. nitin@example.com" },
  { id: "label", label: "Has Gmail label", placeholder: "e.g. IMPORTANT" },
  { id: "any", label: "Any new inbox email", placeholder: "" },
];

function gmailTriggerConfig(node = {}) {
  const rule = node.triggerRule || (node.triggerSubject ? "subject" : "");
  const value = node.triggerValue ?? node.triggerSubject ?? "";
  const configured = rule === "any" || (Boolean(rule) && Boolean(String(value).trim()));
  return { rule, value, configured };
}

function gmailTriggerSummary(node = {}) {
  const { rule, value, configured } = gmailTriggerConfig(node);
  if (!configured) return "Needs setup";
  if (rule === "any") return "Any new inbox email";
  return `${GMAIL_TRIGGER_RULES.find(item => item.id === rule)?.label || "Rule"}: ${value}`;
}

function getNodeType(agid) {
  if (!agid) return NODE_TYPES.agent;
  if (TRIGGER_IDS.has(agid)) return NODE_TYPES.trigger;
  if (LOGIC_IDS.has(agid))   return NODE_TYPES.logic;
  if (OUTPUT_IDS.has(agid))  return NODE_TYPES.output;
  return NODE_TYPES.agent;
}

// Canvas flows created outside the builder (for example from Home chat) must
// carry positions.  Older generated flows did not, causing every node to be
// rendered at the origin.  Repair those flows on load so they remain usable;
// the corrected positions are persisted the next time the user saves.
function ensureCanvasLayout(rawNodes = []) {
  const nodes = Array.isArray(rawNodes) ? rawNodes : [];
  const seen = new Set();
  const hasMissingOrCollidingPosition = nodes.some(node => {
    const x = Number(node.x);
    const y = Number(node.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return true;
    const key = `${x}:${y}`;
    if (seen.has(key)) return true;
    seen.add(key);
    return false;
  });
  if (!hasMissingOrCollidingPosition) return nodes;

  return nodes.map((node, index) => ({
    ...node,
    x: 80 + index * 320,
    // A trigger phone is taller than a regular node, so centre its successor
    // beside it instead of putting the cards on top of one another.
    y: index === 0 ? 120 : 155,
  }));
}

// Renders an agent icon: <img> for data URLs, JSX/emoji otherwise
function AgentIcon({ icon, size = 18 }) {
  if (typeof icon === "string" && icon.startsWith("data:")) {
    return <img src={icon} alt="" style={{ width: size, height: size, objectFit: "cover", borderRadius: "inherit", display: "block" }} />;
  }
  return icon || null;
}

function ModelPickerInline({ onChangeModel }) {
  const { models, selectedModelId } = useAI();
  const [localModelId, setLocalModelId] = useState(null);
  const effective = localModelId || selectedModelId || '';
  useEffect(() => { if (onChangeModel) onChangeModel(localModelId || selectedModelId || null); }, [localModelId, selectedModelId]);
  return (
    <select value={effective} onChange={e => setLocalModelId(e.target.value || null)}
      style={{ marginLeft: 8, padding: '6px 8px', borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface2, color: T.ink3, fontSize: 12, fontFamily: 'inherit' }}>
      <option value="">⚡ Auto</option>
      {models && models.map(m => <option key={m.id} value={m.id}>{m.name || m.id}</option>)}
    </select>
  );
}

// ─── Built-in AI Agents ───────────────────────────────────────────────────────
const BUILTIN_AGENT_PALETTE = [
  { id: "email",        name: "Draft email",            tag: "Draft & reply",    icon: <Mail size={15} />,        color: "#4a6fa5", bg: "var(--app-primary-soft)", bd: "var(--app-primary-soft-2)" },
  { id: "research",     name: "Search & verify",        tag: "Find & verify",    icon: <Search size={15} />,      color: "var(--lyra-color-primary)", bg: "var(--app-primary-soft)", bd: "var(--app-primary-soft-2)" },
  { id: "code",         name: "Code Assistant",         tag: "Dev & debug",      icon: <FileText size={15} />,    color: "#8b5cf6", bg: "var(--app-primary-soft)", bd: "var(--app-primary-soft)" },
  { id: "workflow",     name: "Automate workflow",      tag: "Automate tasks",   icon: <Settings2 size={15} />,   color: "var(--lyra-color-warning)", bg: "var(--lyra-color-warning-bg)", bd: "var(--app-warning-border)" },
  { id: "data",         name: "Analyse data",           tag: "Insights & charts",icon: <BarChart2 size={15} />,   color: "var(--lyra-color-success)", bg: "var(--lyra-color-success-bg)", bd: "var(--app-success-border)" },
  { id: "writing",      name: "Generate content",       tag: "Content & docs",   icon: <PenLine size={15} />,     color: "#ec4899", bg: "var(--lyra-color-surface-2)", bd: "#fbcfe8" },
  { id: "creative",     name: "Creative Ideator",       tag: "Brainstorm",       icon: <Lightbulb size={15} />,   color: "var(--lyra-color-warning)", bg: "var(--lyra-color-warning-bg)", bd: "#fed7aa" },
  { id: "finance",      name: "Finance Agent",          tag: "Budget & spending", icon: <DollarSign size={15} />, color: "#4a7c59", bg: "var(--lyra-color-success-bg)", bd: "var(--app-success-border)" },
  { id: "meeting",      name: "Meeting Agent",          tag: "Notes & actions",  icon: <CalendarDays size={15} />,color: "#6b5fa6", bg: "var(--app-primary-soft)", bd: "var(--app-primary-soft)" },
  { id: "bookmarks",    name: "Bookmark Agent",         tag: "Links & digest",   icon: <Bookmark size={15} />,    color: "#9c7a4a", bg: "var(--lyra-color-surface-2)", bd: "var(--lyra-color-warning-bg)" },
  { id: "presentation", name: "Presentation Builder",   tag: "Slides & decks",   icon: <BookOpen size={15} />,    color: "#9c4a6b", bg: "var(--lyra-color-surface-2)", bd: "#fbcfe8" },
  { id: "pdf",          name: "PDF & Attachment Agent", tag: "Read & extract",   icon: <FileText size={15} />,    color: "#3d7a8a", bg: "var(--app-primary-soft)", bd: "var(--app-primary-soft-2)" },
];
const BUILTIN_BY_ID = Object.fromEntries(BUILTIN_AGENT_PALETTE.map(a => [a.id, a]));

// ─── Static palette (Triggers / Logic / Outputs) ──────────────────────────────
const PALETTE_STATIC = [
  {
    cat: "Triggers", items: [
      { id: "mobile-trigger", name: "Trigger", tag: "Run on a Telegram message, email or schedule", icon: <Smartphone size={15} />, color: T.amber, bg: T.amberBg, bd: T.amberBd },
      { id: "webhook",  name: "Webhook",   tag: "HTTP trigger", icon: <Zap size={15} />,         color: T.amber, bg: T.amberBg, bd: T.amberBd },
      { id: "schedule", name: "Scheduler", tag: "Cron / time",  icon: <Timer size={15} />,        color: T.accent, bg: T.accentBg, bd: T.accentBd },
      { id: "telegram", name: "Telegram (basic)",  tag: "Reads last message when run", icon: <Send size={15} />,         color: T.accent, bg: T.accentBg, bd: T.accentBd },
      { id: "whatsapp", name: "WhatsApp",  tag: "Message in",   icon: <MessageSquare size={15} />, color: T.green, bg: T.greenBg, bd: T.greenBd },
    ]
  },
  {
    cat: "Logic", items: [
      { id: "condition", name: "Condition", tag: "If / Else",       icon: <GitMerge size={15} />, color: T.amber, bg: T.amberBg, bd: T.amberBd, isCond: true },
      { id: "delay",     name: "Wait",      tag: "Pause flow",      icon: <Hourglass size={15} />, color: T.muted, bg: "var(--lyra-color-surface-2)", bd: T.border },
      { id: "merge",     name: "Merge",     tag: "Combine inputs",  icon: <Link2 size={15} />,    color: T.accent, bg: T.accentBg, bd: T.accentBd },
    ]
  },
  {
    cat: "Outputs", items: [
      { id: "gmail",        name: "Gmail Send",    tag: "Send email",   icon: <Mail size={15} />,    color: T.red,    bg: T.redBg,    bd: T.redBd    },
      { id: "telegramSend", name: "Telegram Send", tag: "Send message", icon: <Send size={15} />,    color: "#229ED9", bg: "var(--lyra-color-surface-2)",  bd: "#9ED8F0"  },
      { id: "notion",       name: "Notion",        tag: "Save to page", icon: <FileText size={15} />,color: T.text,   bg: "var(--lyra-color-surface-2)",  bd: T.border   },
      { id: "slackout",     name: "Post to Slack", tag: "Channel msg",  icon: <Hash size={15} />,    color: T.purple, bg: T.purpleBg, bd: T.purpleBd },
      { id: "webhkout",     name: "Webhook Out",   tag: "HTTP POST",    icon: <Globe size={15} />,   color: T.accent, bg: T.accentBg, bd: T.accentBd },
    ]
  },
];

// ─── Sample outputs ───────────────────────────────────────────────────────────
const SAMPLE_OUT = {
  email: "✉️ Email drafted — professional tone, subject line generated.",
  research: "🔍 Research complete — 5 key findings, 3 sources cited.",
  code: "💻 Code generated — reviewed for best practices.",
  workflow: "⚙️ Workflow defined — 4 steps automated.",
  data: "📊 Data analyzed — 3 insights, 1 anomaly flagged.",
  writing: "📝 Content written — 450 words, clear structure.",
  creative: "🎨 10 ideas generated — top 3 highlighted.",
  finance: "💰 Budget report ready — 2 savings opportunities found.",
  meeting: "🗓️ Meeting notes structured — 5 action items extracted.",
  bookmarks: "🔖 4 links summarized — tagged by topic.",
  presentation: "🎯 Deck outline ready — 8 slides planned.",
  gmail: "📧 Sent to recipient@company.com ✓",
  notion: "📓 Saved to /Engineering/Reports ✓",
  slackout: "#️⃣ Posted to #engineering ✓",
  webhkout: "🌐 HTTP 200 OK",
  telegramSend: "✈️ Telegram message sent ✓",
  webhook: "⚡ POST received — 200 OK",
  schedule: "⏰ Triggered at 09:00 IST",
  telegram: "✈️ Telegram message received",
  whatsapp: "💬 WhatsApp message received",
  delay: "⏳ Waited 3s, continuing...",
  merge: "🔗 2 inputs merged.",
};

// ─── Condition config ─────────────────────────────────────────────────────────
const COND_FIELDS = [
  { id: "output",       label: "Previous output (full text)" },
  { id: "output.score", label: "output.score" },
  { id: "output.status",label: "output.status" },
  { id: "output.count", label: "output.count" },
  { id: "output.error", label: "output.error" },
  { id: "custom",       label: "Custom field path…" },
];
const COND_OPS = [
  { id: "contains",     label: "contains",            needsValue: true  },
  { id: "not_contains", label: "doesn't contain",     needsValue: true  },
  { id: "equals",       label: "equals",              needsValue: true  },
  { id: "not_equals",   label: "not equals",          needsValue: true  },
  { id: "starts_with",  label: "starts with",         needsValue: true  },
  { id: "ends_with",    label: "ends with",           needsValue: true  },
  { id: "gt",           label: "> (greater than)",    needsValue: true  },
  { id: "lt",           label: "< (less than)",       needsValue: true  },
  { id: "gte",          label: ">= (at least)",       needsValue: true  },
  { id: "lte",          label: "<= (at most)",        needsValue: true  },
  { id: "is_truthy",    label: "is truthy / present", needsValue: false },
  { id: "is_empty",     label: "is empty / missing",  needsValue: false },
];
const DEFAULT_CONDITION = {
  conditionMode: "expression",
  conditionExpr: { field: "output", operator: "contains", value: "", customField: "" },
  conditionRule: "",
};

function getConditionDemoOutput(node) {
  const mode = node.conditionMode || "expression";
  if (mode === "ai") {
    const rule = (node.conditionRule || "").trim();
    if (rule) return `🔀 AI evaluated: "${rule.length > 52 ? rule.slice(0, 52) + "…" : rule}" → ✅ TRUE (demo)`;
    return "🔀 AI condition — no rule set → ✅ TRUE (demo)";
  }
  const expr = node.conditionExpr || {};
  const field = expr.field === "custom" ? (expr.customField || "field") : (expr.field || "output");
  const op = COND_OPS.find(o => o.id === expr.operator);
  const opLabel = op?.label || "contains";
  const val = expr.value || "";
  if (!op?.needsValue) return `🔀 ${field} ${opLabel} → ✅ TRUE (demo)`;
  if (val) return `🔀 ${field} ${opLabel} "${val}" → ✅ TRUE (demo)`;
  return "🔀 Condition not fully configured → ✅ TRUE (demo)";
}

function getPaletteItem(id, agentsList = []) {
  if (!id) return null;
  if (id.startsWith("custom:")) {
    const mongoId = id.replace("custom:", "");
    const found = agentsList.find(a => (a._id || a.id) === mongoId);
    return { id, name: found?.name || "Custom Agent", tag: found?.description?.slice(0, 22) || "My Agent", icon: found?.icon || <Bot size={15} />, color: found?.color || T.accent, bg: T.accentBg, bd: T.accentBd, isCustom: true };
  }
  if (BUILTIN_BY_ID[id]) return BUILTIN_BY_ID[id];
  for (const cat of PALETTE_STATIC) { const f = cat.items.find(i => i.id === id); if (f) return f; }
  return null;
}

// ─── SVG Connector Layer ──────────────────────────────────────────────────────
function ConnectorLayer({ nodes, connections, runningFrom, doneConns, onDeleteConn, tmpConn }) {
  return (
    <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible" }}>
      <defs>
        <style>{`@keyframes dashFlow{to{stroke-dashoffset:-20}} .c-run{animation:dashFlow 0.6s linear infinite}`}</style>
        {["arr", "arr-run", "arr-done"].map((id, i) => (
          <marker key={id} id={id} markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
            <polygon points="0 0,8 3,0 6" fill={[T.border2, T.amber, T.green][i]} />
          </marker>
        ))}
      </defs>
      {connections.map((conn, idx) => {
        const fn = nodes.find(n => n.id === conn.from);
        const tn = nodes.find(n => n.id === conn.to);
        if (!fn || !tn) return null;
        const fEl = document.getElementById("fn-" + conn.from);
        const tEl = document.getElementById("fn-" + conn.to);
        if (!fEl || !tEl) return null;
        const fw = fEl.offsetWidth, fh = fEl.offsetHeight, th = tEl.offsetHeight;
        let oy = fn.y + fh / 2;
        if (conn.port === "true")  oy = fn.y + fh * 0.38;
        if (conn.port === "false") oy = fn.y + fh * 0.62;
        const x1 = fn.x + fw, y1 = oy, x2 = tn.x, y2 = tn.y + th / 2;
        const dx = Math.max(50, Math.abs(x2 - x1) * 0.45);
        const d = `M${x1} ${y1} C${x1+dx} ${y1} ${x2-dx} ${y2} ${x2} ${y2}`;
        const isRun  = runningFrom === conn.from;
        const isDone = doneConns.has(conn.from);
        const stroke = isRun ? T.amber : isDone ? T.green : T.border2;
        const marker = `url(#${isRun ? "arr-run" : isDone ? "arr-done" : "arr"})`;
        const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
        return (
          <g key={idx}>
            <path d={d} fill="none" stroke="transparent" strokeWidth="14" style={{ pointerEvents: "stroke", cursor: "pointer" }} onDoubleClick={() => onDeleteConn(idx)} />
            <path d={d} fill="none" stroke={stroke} strokeWidth="1.8" strokeDasharray={isRun ? "7 4" : "none"} className={isRun ? "c-run" : ""} markerEnd={marker} />
            {conn.label && (() => {
              const ll = conn.label.length;
              return (
                <g>
                  <rect x={mx - ll*3} y={my-9} width={ll*6} height={16} rx="4" fill={T.white} stroke={T.border} strokeWidth="1" />
                  <text x={mx} y={my+4} textAnchor="middle" fontSize="10" fill={T.muted} fontFamily="system-ui,sans-serif">{conn.label}</text>
                </g>
              );
            })()}
            {(conn.port === "true" || conn.port === "false") && (
              <text x={x1+8} y={y1-5} fontSize="9" fontWeight="700" fontFamily="system-ui,sans-serif" fill={conn.port === "true" ? T.green : T.red}>
                {conn.port.toUpperCase()}
              </text>
            )}
          </g>
        );
      })}
      {tmpConn && (() => {
        const fn = nodes.find(n => n.id === tmpConn.from); if (!fn) return null;
        const fEl = document.getElementById("fn-" + fn.id);
        const fw = fEl?.offsetWidth || 210, fh = fEl?.offsetHeight || 130;
        const x1 = fn.x + fw, y1 = fn.y + fh/2, x2 = tmpConn.ex, y2 = tmpConn.ey;
        const dx = Math.max(50, Math.abs(x2-x1)*0.45);
        return <path d={`M${x1} ${y1} C${x1+dx} ${y1} ${x2-dx} ${y2} ${x2} ${y2}`} fill="none" stroke={T.accent} strokeWidth="1.8" strokeDasharray="6 3" />;
      })()}
    </svg>
  );
}

// ─── Port ─────────────────────────────────────────────────────────────────────
function Port({ style: extraStyle, onMouseDown, title, color, bg }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseDown={onMouseDown} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} title={title}
      style={{ position: "absolute", width: 13, height: 13, borderRadius: "50%", background: hov ? (color || T.accent) : (bg || T.white), border: `2px solid ${hov ? (color || T.accent) : T.border2}`, cursor: "crosshair", transition: "all 0.15s", zIndex: 10, boxShadow: hov ? `0 0 0 3px ${(color||T.accent)}20` : "none", ...extraStyle }} />
  );
}

// ─── Condition Node Body ──────────────────────────────────────────────────────
function ConditionNodeBody({ node, onChange }) {
  const mode = node.conditionMode || "expression";
  const expr = node.conditionExpr || { field: "output", operator: "contains", value: "", customField: "" };
  const rule = node.conditionRule || "";
  const op   = COND_OPS.find(o => o.id === expr.operator) || COND_OPS[0];

  const setMode = (m) => { node.conditionMode = m; onChange(); };
  const setExpr = (patch) => { node.conditionExpr = { ...expr, ...patch }; onChange(); };
  const setRule = (v) => { node.conditionRule = v; node.brief = v; onChange(); };

  const inputSx = { width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 5, padding: "4px 7px", fontSize: 10, color: T.text, outline: "none", fontFamily: "inherit", boxSizing: "border-box", transition: "border-color 0.15s" };

  return (
    <div onMouseDown={e => e.stopPropagation()}>
      <div style={{ display: "flex", gap: 4, marginBottom: 9 }}>
        {[{ id: "expression", label: "⚡ Expression" }, { id: "ai", label: "🤖 AI Rule" }].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
            style={{ flex: 1, padding: "4px 0", fontSize: 9, fontWeight: 700, fontFamily: "inherit", borderRadius: 5, cursor: "pointer", border: `1.5px solid ${mode === m.id ? T.accent : T.border}`, background: mode === m.id ? T.accentBg : T.white, color: mode === m.id ? T.accent : T.muted, transition: "all 0.15s" }}>
            {m.label}
          </button>
        ))}
      </div>
      {mode === "expression" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.dim, marginBottom: 2 }}>Field</div>
            <select value={expr.field} onChange={e => setExpr({ field: e.target.value })} style={inputSx} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border}>
              {COND_FIELDS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          {expr.field === "custom" && (
            <input value={expr.customField || ""} onChange={e => setExpr({ customField: e.target.value })} placeholder="e.g. output.data.items[0].score" style={{ ...inputSx, fontFamily: "monospace", fontSize: 10 }} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border} />
          )}
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.dim, marginBottom: 2 }}>Operator</div>
            <select value={expr.operator} onChange={e => setExpr({ operator: e.target.value })} style={inputSx} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border}>
              {COND_OPS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          {op.needsValue && (
            <div>
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.dim, marginBottom: 2 }}>Value</div>
              <input value={expr.value || ""} onChange={e => setExpr({ value: e.target.value })} placeholder={["gt","lt","gte","lte"].includes(expr.operator) ? "e.g. 5" : "e.g. blocker"} style={inputSx} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border} />
            </div>
          )}
          {(expr.value || !op.needsValue) && (
            <div style={{ background: T.amberBg, border: `1px solid ${T.amberBd}`, borderRadius: 5, padding: "4px 8px", fontSize: 9, color: "var(--lyra-color-warning)", lineHeight: 1.5, marginTop: 1 }}>
              IF <code style={{ fontFamily: "monospace", fontSize: 9, background: "rgba(0,0,0,0.05)", padding: "0 3px", borderRadius: 3 }}>{expr.field === "custom" ? (expr.customField || "field") : expr.field}</code>
              {" "}{op.label}
              {op.needsValue && expr.value ? <> <code style={{ fontFamily: "monospace", fontSize: 9, background: "rgba(0,0,0,0.05)", padding: "0 3px", borderRadius: 3 }}>"{expr.value}"</code></> : null}
              {" "}→ branch TRUE / FALSE
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.dim, marginBottom: 1 }}>Natural Language Rule</div>
          <textarea value={rule} onChange={e => setRule(e.target.value)} rows={3} placeholder={"e.g.\n• If the output mentions any blockers\n• If sentiment score > 0.7"} style={{ width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 8px", fontSize: 10, color: T.text, outline: "none", resize: "none", fontFamily: "inherit", lineHeight: 1.5, transition: "border-color 0.15s", boxSizing: "border-box" }} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border} />
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6, background: T.purpleBg, border: `1px solid ${T.purpleBd}`, borderRadius: 6, padding: "6px 8px" }}>
            <Bot size={10} color={T.purple} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 9, color: T.purple, lineHeight: 1.45 }}>A fast AI agent receives the previous node's output and evaluates this rule at runtime, returning <strong>true</strong> or <strong>false</strong>.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Telegram status (shared by the Telegram trigger and the Telegram Send output) ──
function useTelegramStatus() {
  const [st, setSt] = useState({ loading: true, connected: false, botUsername: null });
  useEffect(() => {
    let live = true;
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    if (!token) { setSt({ loading: false, connected: false, botUsername: null }); return undefined; }
    fetch(`${API_ORIGIN}/telegram/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok && (r.headers.get("content-type") || "").includes("json") ? r.json() : null))
      .then(d => { if (live) setSt({ loading: false, connected: !!d?.connected, botUsername: d?.botUsername || null }); })
      .catch(() => { if (live) setSt({ loading: false, connected: false, botUsername: null }); });
    return () => { live = false; };
  }, []);
  return st;
}

function TelegramSendInfo() {
  const tg = useTelegramStatus();
  const box = { borderRadius: 8, padding: "7px 9px", fontSize: 10, lineHeight: 1.45 };
  if (tg.loading) return null;
  return tg.connected
    ? <div style={{ ...box, background: T.greenBg, border: `1px solid ${T.greenBd}`, color: T.ink3 }}>✈️ Sends to your Telegram{tg.botUsername ? <> (<b>@{tg.botUsername}</b>)</> : null}. Long results are split into several messages.</div>
    : <div style={{ ...box, background: T.amberBg, border: `1px solid ${T.amberBd}`, color: T.ink3 }}>Connect Telegram so this step can deliver messages to you.<button onClick={() => window.location.assign("/integrations#telegram")} style={{ border: 0, background: "transparent", color: T.accent, fontWeight: 800, cursor: "pointer", padding: 0, marginLeft: 4, fontFamily: "inherit" }}>Open Telegram setup ›</button></div>;
}

// ─── Flow Node ────────────────────────────────────────────────────────────────
function FlowNode({ telegramConnected, node, isSelected, isRunning, isDone, output, scheduleInfo, onMouseDown, onDoubleClick, onRemove, onConnectStart, onConditionChange, onTriggerChange, onTriggerConfigure, onScheduleSetup, gmailConnected, flowSaved, agentOptions = [], onAgentChange }) {
  const item = node.item;
  const [, forceUpdate] = useState(0);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const initialGmail = gmailTriggerConfig(node);
  const [gmailRule, setGmailRule] = useState(initialGmail.rule || "subject");
  const [gmailValue, setGmailValue] = useState(initialGmail.value || "");
  const [tgKeyword, setTgKeyword] = useState(node.triggerChannel === "telegram" ? (node.triggerValue || "") : "");
  const isScheduler = node.agid === "schedule";
  const isCondition = node.agid === "condition";
  const isMobileTrigger = node.agid === "mobile-trigger";
  const isAgentNode = !isMobileTrigger && !isScheduler && !isCondition && !OUTPUT_IDS.has(node.agid);
  const nt = getNodeType(node.agid);
  const outBg   = output?.startsWith("❌") ? T.redBg   : T.greenBg;
  const outBd   = output?.startsWith("❌") ? T.redBd   : T.greenBd;
  const outText = output?.startsWith("❌") ? T.red     : T.green;
  const condSubtitle = isCondition ? (node.conditionMode === "ai" ? "AI-evaluated rule" : "Expression rule") : item.tag;

  // A trigger is deliberately a phone, not a generic workflow card with a
  // phone illustration squeezed inside it.  Each source is visible at a glance
  // and opens its detailed setup on double-click.
  if (isMobileTrigger) {
    const openGmailSetup = () => {
      const config = gmailTriggerConfig(node);
      setGmailRule(config.rule || "subject");
      setGmailValue(config.value || "");
      setEditingTrigger("gmail");
    };
    const gmailNeedsValue = gmailRule !== "any";
    const gmailReady = Boolean(gmailConnected) && (!gmailNeedsValue || Boolean(gmailValue.trim()));
    const tgReady = Boolean(telegramConnected) && tgKeyword.trim().length >= 2;
    if (editingTrigger === "telegram") {
      return (
        <div id={"fn-" + node.id} onMouseDown={onMouseDown}
          style={{ position:"absolute", left:node.x, top:node.y, width:222, background:T.white, border:`2px solid ${isSelected ? T.accent : T.ink}`, borderRadius:28, padding:8, boxShadow:isSelected ? `0 0 0 3px ${T.accentLight}, 0 10px 30px rgba(15,23,42,.12)` : "0 5px 18px rgba(15,23,42,.1)" }}>
          <div style={{ width:42, height:4, borderRadius:99, background:T.border2, margin:"3px auto 9px" }} />
          <div style={{ display:"flex", alignItems:"center", gap:6, padding:"0 6px 10px" }}>
            <button onMouseDown={e=>e.stopPropagation()} onClick={() => setEditingTrigger(null)} style={{ border:0, background:"transparent", cursor:"pointer", color:T.ink3, padding:2, fontSize:16 }}>‹</button>
            <div style={{ flex:1 }}><div style={{ fontSize:14, fontWeight:800, color:T.text }}>Telegram trigger</div><div style={{ fontSize:9, color:T.muted }}>Run this flow from a message</div></div>
            <button onMouseDown={e=>e.stopPropagation()} onClick={onRemove} style={{ border:0, background:"transparent", color:T.dim, cursor:"pointer", padding:3, display:"flex" }}><X size={15}/></button>
          </div>
          <div onMouseDown={e=>e.stopPropagation()} style={{ display:"grid", gap:9, padding:"0 3px 4px" }}>
            <div>
              <label style={{ display:"block", fontSize:8.5, letterSpacing:".07em", textTransform:"uppercase", fontWeight:800, color:T.dim, marginBottom:4 }}>When I send a message starting with…</label>
              <input value={tgKeyword} onChange={e=>setTgKeyword(e.target.value)} placeholder="e.g. report" style={{ width:"100%", boxSizing:"border-box", border:`1px solid ${T.border}`, borderRadius:8, padding:"7px 8px", background:T.white, color:T.text, fontSize:10, fontFamily:"inherit", outline:"none" }} />
            </div>
            <div style={{ fontSize:9, color:T.muted, lineHeight:1.4 }}>Send <b>{tgKeyword.trim() || "report"} weekly numbers</b> to your bot: the flow runs with “weekly numbers” as its input. Add a <b>Telegram Send</b> output to get the result back, or it is replied automatically.</div>
            {!telegramConnected && <div style={{ background:T.amberBg, border:`1px solid ${T.amberBd}`, borderRadius:8, padding:"7px 8px", fontSize:9, color:T.ink3, lineHeight:1.4 }}>Connect Telegram before this trigger can listen.<button onClick={() => window.location.assign("/integrations#telegram")} style={{ border:0, background:"transparent", color:T.accent, fontWeight:800, cursor:"pointer", padding:0, marginLeft:4, fontFamily:"inherit" }}>Open Telegram setup ›</button></div>}
            <button disabled={!tgReady} onClick={() => { onTriggerConfigure?.({ triggerChannel:"telegram", triggerRule:"keyword", triggerValue:tgKeyword.trim(), triggerEnabled:true }); setEditingTrigger(null); }} style={{ width:"100%", border:0, borderRadius:8, padding:"8px", background:tgReady ? T.accent : T.border2, color:tgReady ? T.white : T.dim, fontSize:10.5, fontWeight:800, cursor:tgReady ? "pointer" : "not-allowed", fontFamily:"inherit" }}>Save & activate</button>
          </div>
        </div>
      );
    }
    if (editingTrigger === "gmail") {
      return (
        <div id={"fn-" + node.id} onMouseDown={onMouseDown}
          style={{ position:"absolute", left:node.x, top:node.y, width:222, background:T.white, border:`2px solid ${isSelected ? T.accent : T.ink}`, borderRadius:28, padding:8, boxShadow:isSelected ? `0 0 0 3px ${T.accentLight}, 0 10px 30px rgba(15,23,42,.12)` : "0 5px 18px rgba(15,23,42,.10)", cursor:"grab", userSelect:"none" }}>
          <div style={{ width:42, height:4, borderRadius:99, background:T.border2, margin:"3px auto 9px" }} />
          <div style={{ display:"flex", alignItems:"center", gap:6, padding:"0 6px 10px" }}>
            <button onMouseDown={e=>e.stopPropagation()} onClick={() => setEditingTrigger(null)} style={{ border:0, background:"transparent", cursor:"pointer", color:T.ink3, padding:2, fontSize:16 }}>‹</button>
            <div style={{ flex:1 }}><div style={{ fontSize:14, fontWeight:800, color:T.text }}>Gmail trigger</div><div style={{ fontSize:9, color:T.muted }}>Configure when this flow runs</div></div>
            <button onMouseDown={e=>e.stopPropagation()} onClick={onRemove} style={{ border:0, background:"transparent", color:T.dim, cursor:"pointer", padding:3, display:"flex" }}><X size={15}/></button>
          </div>
          <div onMouseDown={e=>e.stopPropagation()} style={{ display:"grid", gap:9, padding:"0 3px 4px" }}>
            <div>
              <label style={{ display:"block", fontSize:8.5, letterSpacing:".07em", textTransform:"uppercase", fontWeight:800, color:T.dim, marginBottom:4 }}>When a new Gmail message…</label>
              <select value={gmailRule} onChange={e=>setGmailRule(e.target.value)} style={{ width:"100%", boxSizing:"border-box", border:`1px solid ${T.border}`, borderRadius:8, padding:"7px 8px", background:T.white, color:T.text, fontSize:10, fontFamily:"inherit" }}>
                {GMAIL_TRIGGER_RULES.map(rule => <option key={rule.id} value={rule.id}>{rule.label}</option>)}
              </select>
            </div>
            {gmailNeedsValue && <div>
              <label style={{ display:"block", fontSize:8.5, letterSpacing:".07em", textTransform:"uppercase", fontWeight:800, color:T.dim, marginBottom:4 }}>Required value</label>
              <input value={gmailValue} onChange={e=>setGmailValue(e.target.value)} placeholder={GMAIL_TRIGGER_RULES.find(rule=>rule.id===gmailRule)?.placeholder} style={{ width:"100%", boxSizing:"border-box", border:`1px solid ${T.border}`, borderRadius:8, padding:"7px 8px", background:T.white, color:T.text, fontSize:10, fontFamily:"inherit", outline:"none" }} />
            </div>}
            {!gmailConnected && <div style={{ background:T.amberBg, border:`1px solid ${T.amberBd}`, borderRadius:8, padding:"7px 8px", fontSize:9, color:T.ink3, lineHeight:1.4 }}>Connect Gmail before this trigger can listen.<button onClick={() => window.location.assign("/integrations#gmail")} style={{ border:0, background:"transparent", color:T.accent, fontWeight:800, cursor:"pointer", padding:0, marginLeft:4, fontFamily:"inherit" }}>Open Gmail setup ›</button></div>}
            {gmailRule === "any" && <div style={{ fontSize:9, color:T.muted, lineHeight:1.4 }}>Only emails arriving after activation will run this flow. Existing inbox mail is never replayed.</div>}
            <button disabled={!gmailReady} onClick={() => { onTriggerConfigure?.({ triggerChannel:"gmail", triggerRule:gmailRule, triggerValue:gmailRule === "any" ? "" : gmailValue.trim(), triggerSubject:gmailRule === "subject" ? gmailValue.trim() : "", triggerEnabled: gmailReady, lastMessageId: undefined }); setEditingTrigger(null); }} style={{ width:"100%", border:0, borderRadius:8, padding:"8px", background:gmailReady ? T.accent : T.border2, color:gmailReady ? T.white : T.dim, fontSize:10.5, fontWeight:800, cursor:gmailReady ? "pointer" : "not-allowed", fontFamily:"inherit" }}>Save & activate Gmail trigger</button>
          </div>
        </div>
      );
    }
    const sourceRows = [
      { id: "gmail", icon: "✉", label: "Gmail", detail: gmailTriggerSummary(node), canActivate: Boolean(gmailConnected) && gmailTriggerConfig(node).configured },
      { id: "telegram", icon: "✈", label: "Telegram", detail: node.triggerChannel === "telegram" && node.triggerValue ? `Message starts with “${node.triggerValue}”` : "Message with a keyword", canActivate: Boolean(telegramConnected) && node.triggerChannel === "telegram" && Boolean(node.triggerValue) },
      { id: "whatsapp", icon: "◌", label: "WhatsApp", detail: "New message", canActivate: false },
      { id: "schedule", icon: "◷", label: "Scheduler", detail: scheduleInfo?.label || "Choose time", canActivate: true },
    ];
    const activeChannel = scheduleInfo ? "schedule" : (node.triggerChannel || "gmail");
    const gmailConfigured = gmailTriggerConfig(node).configured;
    const isActuallyListening = Boolean(flowSaved) && (activeChannel === "gmail" ? Boolean(node.triggerEnabled && gmailConfigured && gmailConnected) : activeChannel === "telegram" ? Boolean(node.triggerEnabled && node.triggerValue && telegramConnected) : activeChannel === "schedule" ? Boolean(scheduleInfo?.enabled) : Boolean(node.triggerEnabled));
    return (
      <div id={"fn-" + node.id} onMouseDown={onMouseDown} onDoubleClick={onDoubleClick}
        style={{ position:"absolute", left:node.x, top:node.y, width:222, background:T.white, border:`2px solid ${isRunning ? T.amber : isDone ? T.green : isSelected ? T.accent : T.ink}`, borderRadius:28, padding:8, boxShadow:isSelected ? `0 0 0 3px ${T.accentLight}, 0 10px 30px rgba(15,23,42,.12)` : "0 5px 18px rgba(15,23,42,.10)", cursor:"grab", userSelect:"none" }}>
        <div style={{ width:42, height:4, borderRadius:99, background:T.border2, margin:"3px auto 9px" }} />
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 6px 8px" }}>
          <div><div style={{ fontSize:14, fontWeight:800, color:T.text }}>Trigger</div><div style={{ fontSize:9, color:T.muted }}>{isActuallyListening ? "Saved & listening" : activeChannel === "gmail" && gmailConfigured ? "Configured — save and activate" : "Choose a source"}</div></div>
          <button onMouseDown={e=>e.stopPropagation()} onClick={onRemove} style={{ border:0, background:"transparent", color:T.dim, cursor:"pointer", padding:3, display:"flex" }}><X size={15}/></button>
        </div>
        <div style={{ display:"grid", gap:6 }}>
          {sourceRows.map(source => {
            const selected = activeChannel === source.id;
            const active = selected && (source.id === "schedule" ? Boolean(scheduleInfo?.enabled) : Boolean(node.triggerEnabled && (source.id !== "gmail" || (gmailConfigured && !!gmailConnected)) && (source.id !== "telegram" || (node.triggerValue && telegramConnected))));
            return <button key={source.id} onMouseDown={e=>e.stopPropagation()} onClick={() => {
              if (source.id === "gmail") { openGmailSetup(); return; }
              if (source.id === "telegram") { setTgKeyword(node.triggerChannel === "telegram" ? (node.triggerValue || "") : ""); setEditingTrigger("telegram"); return; }
              if (source.id === "schedule") { onScheduleSetup?.(); return; }
              if (!source.canActivate) { window.location.assign(`/integrations#${source.id}`); return; }
              onTriggerChange?.(source.id, selected ? !node.triggerEnabled : true);
            }} style={{ width:"100%", border:`1px solid ${selected ? T.accent : T.border}`, background:selected ? T.accentBg : T.white, borderRadius:10, padding:"8px 9px", display:"flex", alignItems:"center", gap:8, textAlign:"left", cursor:"pointer", fontFamily:"inherit" }}>
              <span style={{ width:24, height:24, borderRadius:7, display:"grid", placeItems:"center", background:selected ? T.white : T.surface, fontSize:12 }}>{source.icon}</span>
              <span style={{ flex:1, minWidth:0 }}><b style={{ display:"block", fontSize:10.5, color:T.text }}>{source.label}</b><span style={{ fontSize:8.5, color:T.muted }}>{source.detail}{source.canActivate ? "" : " · set up"}</span></span>
              {(source.id === "gmail" || source.id === "telegram") && source.canActivate ? <span onClick={e => { e.stopPropagation(); onTriggerConfigure?.({ triggerEnabled: !node.triggerEnabled }); }} aria-label={`${source.label} ${active ? "enabled" : "disabled"}`} style={{ width:28, height:16, borderRadius:99, padding:2, background:active ? T.green : T.border2, display:"flex", justifyContent:active ? "flex-end" : "flex-start" }}><span style={{ width:12, height:12, borderRadius:"50%", background:T.white, boxShadow:"0 1px 2px rgba(0,0,0,.18)" }}/></span> : source.canActivate ? <span aria-label={`${source.label} ${active ? "enabled" : "disabled"}`} style={{ width:28, height:16, borderRadius:99, padding:2, background:active ? T.green : T.border2, display:"flex", justifyContent:active ? "flex-end" : "flex-start" }}><span style={{ width:12, height:12, borderRadius:"50%", background:T.white, boxShadow:"0 1px 2px rgba(0,0,0,.18)" }}/></span> : <span style={{ fontSize:8, fontWeight:700, color:T.amber }}>{source.id === "gmail" ? "Set up ›" : "Set up ›"}</span>}
            </button>;
          })}
        </div>
        <div style={{ fontSize:8.5, color:T.muted, textAlign:"center", padding:"9px 4px 2px" }}>Double-click for filters and channel setup</div>
        <div onMouseDown={e=>e.stopPropagation()} onClick={e=>onConnectStart(e,node.id,"out")} style={{ position:"absolute", right:-8, top:"50%", width:14, height:14, borderRadius:"50%", background:T.white, border:`2px solid ${T.border2}`, cursor:"crosshair" }} />
      </div>
    );
  }

  return (
    <div id={"fn-" + node.id} onMouseDown={onMouseDown} onDoubleClick={onDoubleClick}
      style={{ position: "absolute", left: node.x, top: node.y, width: isCondition ? 224 : isMobileTrigger ? 250 : 210, background: T.white, border: `1.5px solid ${isRunning ? T.amber : isDone ? T.green : isSelected ? T.accent : isScheduler && scheduleInfo ? T.accent : T.border}`, borderLeft: `4px solid ${nt.border}`, borderRadius: 12, boxShadow: isRunning ? `0 0 0 3px ${T.amberBg}, 0 4px 20px rgba(245,158,11,0.12)` : isSelected ? `0 0 0 3px ${T.accentLight}, 0 2px 8px rgba(58,107,92,0.08)` : isScheduler && scheduleInfo ? `0 0 0 3px ${T.accentLight}` : "0 1px 4px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.02)", cursor: "grab", userSelect: "none", transition: "border-color 0.2s, box-shadow 0.2s" }}>
      {/* Header */}
      <div style={{ padding: "10px 12px 8px", borderRadius: "10px 10px 0 0", borderBottom: `1px solid ${T.border}`, background: nt.bg, display: "flex", alignItems: "center", gap: 9 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: nt.bg, border: `1px solid ${nt.border}`, display: "flex", alignItems: "center", justifyContent: "center", color: nt.text, overflow: "hidden" }}><AgentIcon icon={item.icon} size={32} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{item.name}</div>
          <div style={{ fontSize: 10, color: T.muted }}>{condSubtitle}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: nt.bg, color: nt.text, border: `1px solid ${nt.border}` }}>{nt.tag}</span>
          {isDone && !isRunning && <CheckCircle2 size={12} color={T.green} />}
          {isRunning && <Loader2 size={12} color={T.amber} style={{ animation: "spin 0.8s linear infinite" }} />}
          <button onMouseDown={e => e.stopPropagation()} onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", borderRadius: 4, color: T.dim, display: "flex", transition: "color 0.15s" }} onMouseEnter={e => e.currentTarget.style.color = T.red} onMouseLeave={e => e.currentTarget.style.color = T.dim}><X size={12} /></button>
        </div>
      </div>
      {isAgentNode && <div onMouseDown={e => e.stopPropagation()} style={{ padding:"7px 12px", borderBottom:`1px solid ${T.border}`, background:T.surface }}><select value={node.agid} onChange={e => onAgentChange?.(e.target.value)} style={{ width:"100%", border:`1px solid ${T.border}`, borderRadius:6, background:T.white, color:T.text, fontSize:10, padding:"5px 7px", fontFamily:"inherit" }}>{agentOptions.map(o => <option key={o.id} value={o.id}>{o.group} · {o.name}</option>)}</select></div>}
      {/* Body */}
      <div style={{ padding: "9px 12px 10px" }}>
        {isCondition ? (
          <>
            {(() => {
              const expr = node.conditionExpr || {};
              const op = COND_OPS.find(o => o.id === expr.operator);
              const val = expr.value || "";
              const field = expr.field === "custom" ? (expr.customField || "") : (expr.field || "");
              if (node.conditionMode === "expression" && field && (val || !op?.needsValue)) {
                return <div style={{ fontSize: 10, padding: "5px 7px", background: NODE_TYPES.logic.bg, borderRadius: 5, color: NODE_TYPES.logic.text, marginBottom: 8, fontFamily: "ui-monospace, monospace" }}>IF {field} {op?.label || expr.operator}{op?.needsValue && val ? ` "${val}"` : ""}</div>;
              }
              return null;
            })()}
            <ConditionNodeBody node={node} onChange={() => { forceUpdate(n => n+1); onConditionChange?.(); }} />
          </>
        ) : isScheduler ? (
          scheduleInfo ? (
            <div style={{ background: T.accentBg, border: `1px solid ${T.accentBd}`, borderRadius: 7, padding: "7px 9px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                <Clock size={11} color={T.accent} />
                <span style={{ fontSize: 10, fontWeight: 700, color: T.accent }}>Schedule Active</span>
                {scheduleInfo.enabled === false && <span style={{ fontSize: 9, padding: "1px 5px", background: T.amberBg, color: T.amber, border: `1px solid ${T.amberBd}`, borderRadius: 4 }}>Paused</span>}
              </div>
              <div style={{ fontSize: 10, fontFamily: "monospace", color: T.text, marginBottom: 3 }}>{scheduleInfo.cronExpr}</div>
              {scheduleInfo.label && <div style={{ fontSize: 9, color: T.muted }}>{scheduleInfo.label}</div>}
              {scheduleInfo.nextRunAt && <div style={{ fontSize: 9, color: T.muted, marginTop: 3 }}>Next: {new Date(scheduleInfo.nextRunAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>}
              {scheduleInfo.runCount > 0 && <div style={{ fontSize: 9, color: T.green, marginTop: 2 }}>✓ {scheduleInfo.runCount} run{scheduleInfo.runCount !== 1 ? "s" : ""} completed</div>}
            </div>
          ) : (
            <div style={{ background: T.bg, border: `1.5px dashed ${T.border2}`, borderRadius: 7, padding: "8px 10px", textAlign: "center" }}>
              <Clock size={16} color={T.dim} style={{ margin: "0 auto 4px" }} />
              <div style={{ fontSize: 10, color: T.muted, fontWeight: 600 }}>No schedule set</div>
              <div style={{ fontSize: 9, color: T.dim, marginTop: 2 }}>Double-click to configure</div>
            </div>
          )
        ) : (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: nt.bg, color: nt.text, border: `1px solid ${nt.border}` }}>{item.tag}</span>
            {node.brief && <span style={{ fontSize: 9, color: T.dim, fontStyle: "italic", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.brief}</span>}
          </div>
        )}
        {output && <div style={{ marginTop: 8, background: outBg, border: `1px solid ${outBd}`, borderRadius: 6, padding: "5px 8px", fontSize: 10, color: outText, lineHeight: 1.4, animation: "fadeUp 0.3s ease" }}>{output}</div>}
      </div>
      {/* Ports */}
      {isCondition ? (
        <>
          <Port onMouseDown={e => { e.stopPropagation(); onConnectStart(e, node.id, "true"); }} title="TRUE branch →" color={T.green} bg={T.greenBg} style={{ right: -7, top: "38%", transform: "translateY(-50%)" }} />
          <Port onMouseDown={e => { e.stopPropagation(); onConnectStart(e, node.id, "false"); }} title="FALSE branch →" color={T.red} bg={T.redBg} style={{ right: -7, top: "62%", transform: "translateY(-50%)" }} />
          <div style={{ position: "absolute", right: 8, top: "38%", transform: "translateY(-50%) translateX(0)", fontSize: 8, fontWeight: 800, color: T.green, pointerEvents: "none", marginTop: -12 }}>T</div>
          <div style={{ position: "absolute", right: 8, top: "62%", transform: "translateY(-50%) translateX(0)", fontSize: 8, fontWeight: 800, color: T.red, pointerEvents: "none", marginTop: 2 }}>F</div>
        </>
      ) : (
        <Port onMouseDown={e => { e.stopPropagation(); onConnectStart(e, node.id, "out"); }} title="Drag to connect →" style={{ right: -7, top: "50%", transform: "translateY(-50%)" }} />
      )}
      <div style={{ position: "absolute", left: -7, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, borderRadius: "50%", background: T.white, border: `2px solid ${T.border2}`, zIndex: 10 }} />
    </div>
  );
}

// ─── Node Config Panel ────────────────────────────────────────────────────────
function NodeConfigPanel({ node, onClose, isMobile = false }) {
  const [toggles, setToggles] = useState({ mem: true, log: false, human: false, retry: true });
  const [brief, setBrief]     = useState(node.brief || "");
  const [toEmail, setToEmail] = useState(node.toEmail || "");
  const [triggerSubject, setTriggerSubject] = useState(node.triggerSubject || "");
  const [triggerEnabled, setTriggerEnabled] = useState(!!node.triggerEnabled);
  const [advOpen, setAdvOpen] = useState(false);
  const { models, selectedModelId } = useAI();
  const [modelId, setModelId] = useState(node.model || node.modelId || selectedModelId || "");
  const Toggle = ({ k }) => (
    <div onClick={() => setToggles(t => ({ ...t, [k]: !t[k] }))} style={{ width: 34, height: 19, borderRadius: 10, cursor: "pointer", position: "relative", background: toggles[k] ? T.accent : T.border2, transition: "background 0.2s", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: 2, left: toggles[k] ? 15 : 2, width: 15, height: 15, borderRadius: "50%", background: T.white, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
    </div>
  );
  const inputSx = { width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 8px", fontSize: 11, color: T.text, outline: "none", fontFamily: "inherit" };
  const lblSx   = { fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.dim, marginBottom: 4 };
  if (node.agid === "mobile-trigger") {
    const active = node.triggerChannel || "gmail";
    const channels = [
      { id: "gmail", label: "Gmail", detail: "Inbox message", icon: "✉" },
      { id: "telegram", label: "Telegram", detail: "New message", icon: "✈" },
      { id: "whatsapp", label: "WhatsApp", detail: "New message", icon: "◌" },
      { id: "schedule", label: "Scheduler", detail: "Time based", icon: "◷", native: true },
    ];
    return <div style={{ position: "absolute", top: isMobile ? "auto" : 8, right: 8, left: isMobile ? 8 : "auto", bottom: isMobile ? 8 : "auto", width: isMobile ? "calc(100vw - 16px)" : 280, background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, boxShadow: "0 8px 28px rgba(0,0,0,.14)", zIndex: 500, overflow: "hidden" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px", borderBottom:`1px solid ${T.border}` }}><b style={{ fontSize:14 }}>Trigger</b><button onClick={onClose} style={{ border:0, background:"none", cursor:"pointer" }}><X size={16}/></button></div>
      <div style={{ padding:14 }}>
        <div style={{ fontSize:11, color:T.muted, lineHeight:1.45, marginBottom:12 }}>Choose one source. Connected sources can be enabled here; others open their setup in Integrations.</div>
        {channels.map(c => <button key={c.id} onClick={() => { if (!c.native && c.id !== "gmail") { window.location.assign(`/integrations#${c.id}`); return; } node.triggerChannel = c.id; setTriggerEnabled(!!node.triggerEnabled); }} style={{ width:"100%", display:"flex", alignItems:"center", gap:10, textAlign:"left", padding:"10px 8px", marginBottom:6, border:`1px solid ${active===c.id ? T.accent : T.border}`, borderRadius:10, background:active===c.id ? T.accentBg : T.white, cursor:"pointer", fontFamily:"inherit" }}>
          <span style={{ width:28, height:28, display:"grid", placeItems:"center", borderRadius:8, background:T.surface2 }}>{c.icon}</span><span style={{ flex:1 }}><b style={{ display:"block", fontSize:11, color:T.text }}>{c.label}</b><span style={{ fontSize:10, color:T.muted }}>{c.detail}{c.native ? " · ready" : c.id === "gmail" ? " · connected account required" : " · set up"}</span></span>{active===c.id && <span style={{ color:T.green, fontSize:11 }}>Enabled</span>}</button>)}
        {active === "gmail" && <><div style={lblSx}>Subject contains</div><input value={triggerSubject} onChange={e => { setTriggerSubject(e.target.value); node.triggerSubject = e.target.value; }} placeholder="e.g. trigger" style={{ ...inputSx, boxSizing:"border-box" }}/><div style={{ fontSize:10, color:T.muted, marginTop:8 }}>Gmail push notifications use Pub/Sub when configured; polling remains the fallback.</div></>}
        <button onClick={() => { const next = !triggerEnabled; node.triggerEnabled = next; setTriggerEnabled(next); }} style={{ width:"100%", marginTop:12, padding:"9px", border:0, borderRadius:8, background:triggerEnabled ? T.surface2 : T.accent, color:triggerEnabled ? T.ink : "var(--lyra-color-surface)", fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{triggerEnabled ? "Pause automation" : "Activate automation"}</button>
      </div>
    </div>;
  }
  return (
    <div style={{ position: "absolute", top: isMobile ? "auto" : 8, right: 8, left: isMobile ? 8 : "auto", bottom: isMobile ? 8 : "auto", width: isMobile ? "calc(100vw - 16px)" : 240, background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", zIndex: 500, animation: "fadeUp 0.2s ease", maxHeight: isMobile ? "65vh" : "calc(100vh - 80px)", overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px 10px", borderBottom: `1px solid ${T.border}` }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.text, display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: node.item?.color, display: "inline-flex", width: 18, height: 18, borderRadius: 4, overflow: "hidden", flexShrink: 0 }}><AgentIcon icon={node.item?.icon} size={18} /></span>{node.item?.name}</span>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", padding: 2 }}><X size={14} /></button>
      </div>
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div style={lblSx}>{node.agid === "telegramSend" ? "Message (optional)" : "Instructions"}</div>
          <textarea value={brief} onChange={e => { setBrief(e.target.value); node.brief = e.target.value; }} rows={3} placeholder={node.agid === "telegramSend" ? "e.g. 📰 Your daily report:\n\n{{input}}   ({{input}} = the previous step's result)" : "Describe what this step should do…"} style={{ ...inputSx, resize: "none", lineHeight: 1.5, boxSizing: "border-box" }} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border} />
        </div>
        {node.agid === "telegramSend" && <TelegramSendInfo />}
        {node.agid === "gmail" && (
          <div>
            <div style={lblSx}>To (recipient email)</div>
            <input type="email" value={toEmail} onChange={e => { setToEmail(e.target.value); node.toEmail = e.target.value; }} placeholder="recipient@example.com" style={{ ...inputSx, boxSizing: "border-box" }} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border} />
          </div>
        )}
        <div>
          <div style={lblSx}>Model</div>
          <select
            style={inputSx}
            value={modelId || ''}
            onChange={e => {
              const v = e.target.value || '';
              setModelId(v);
              // persist onto node so canvasData contains model selection when saved
              if (v) node.model = v; else delete node.model;
            }}
          >
            <option value="">⚡ Auto (Best Available)</option>
            {(models || []).map(m => <option key={m.id} value={m.id}>{m.name || m.id}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: T.muted }}>🧠 KultMemory</span><Toggle k="mem" />
        </div>
        <div>
          <div style={lblSx}>Output preview</div>
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: "7px 9px", fontSize: 11, color: T.dim, minHeight: 44, fontStyle: "italic" }}>{node.lastOutput || "Run flow to see output here"}</div>
        </div>
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
          <button onClick={() => setAdvOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
            <span style={lblSx}>Advanced settings</span>
            <span style={{ fontSize: 10, color: T.dim, marginLeft: "auto", transform: advOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.2s" }}>▾</span>
          </button>
          {advOpen && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
              <div><div style={lblSx}>Temperature</div><input type="range" min="0" max="100" defaultValue="70" style={{ width: "100%", accentColor: T.accent }} /></div>
              {[{ k: "log", l: "📊 Log Output" }, { k: "human", l: "⏸ Human Review" }, { k: "retry", l: "🔁 Retry on Fail" }].map(r => (
                <div key={r.k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><span style={{ fontSize: 11, color: T.muted }}>{r.l}</span><Toggle k={r.k} /></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Scheduler Config Panel ───────────────────────────────────────────────────
const PRESETS = [
  { id: "every15m", label: "Every 15 minutes", cron: "*/15 * * * *" },
  { id: "every30m", label: "Every 30 minutes", cron: "*/30 * * * *" },
  { id: "every1h",  label: "Every hour",        cron: "0 * * * *"   },
  { id: "every6h",  label: "Every 6 hours",     cron: "0 */6 * * *" },
  { id: "daily9am", label: "Daily at 9:00 AM",  cron: "0 9 * * *"   },
  { id: "daily6pm", label: "Daily at 6:00 PM",  cron: "0 18 * * *"  },
  { id: "dailyTime", label: "Daily at a time…", cron: ""            },
  { id: "weekdays", label: "Weekdays at 9AM",   cron: "0 9 * * 1-5" },
  { id: "weekdaysTime", label: "Weekdays at a time…", cron: ""      },
  { id: "weekly",   label: "Weekly (Mon 9AM)",  cron: "0 9 * * 1"   },
  { id: "monthly",  label: "Monthly (1st 9AM)", cron: "0 9 1 * *"   },
  { id: "custom",   label: "Custom cron…",      cron: ""             },
];
const TZ_OPTIONS = ["UTC","Asia/Kolkata","America/New_York","America/Los_Angeles","America/Chicago","Europe/London","Europe/Paris","Europe/Berlin","Asia/Tokyo","Asia/Shanghai","Asia/Singapore","Australia/Sydney"];

function parseCronHuman(expr) {
  const map = { "*/15 * * * *":"Every 15 minutes","*/30 * * * *":"Every 30 minutes","0 * * * *":"Every hour","0 */6 * * *":"Every 6 hours","0 9 * * *":"Daily at 9:00 AM","0 18 * * *":"Daily at 6:00 PM","0 9 * * 1-5":"Weekdays at 9:00 AM","0 9 * * 1":"Weekly (Mon 9AM)","0 9 1 * *":"Monthly (1st 9AM)" };
  return map[expr?.trim()] || expr || "";
}

function SchedulerConfigPanel({ node, flowId, scheduleInfo, onScheduleSaved, onScheduleDeleted, onClose, isMobile = false }) {
  const needsSave = !flowId;
  const scheduleTime = (expr) => {
    const [minute = "0", hour = "9"] = String(expr || "").trim().split(/\s+/);
    return /^\d+$/.test(minute) && /^\d+$/.test(hour) ? `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}` : "09:00";
  };
  const initPreset = () => {
    if (!scheduleInfo) return "daily9am";
    const p = PRESETS.find(p => p.cron && p.cron === scheduleInfo.cronExpr);
    if (p) return p.id;
    const parts = String(scheduleInfo.cronExpr || "").trim().split(/\s+/);
    return parts.length === 5 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1]) && parts[2] === "*" && parts[3] === "*" ? (parts[4] === "1-5" ? "weekdaysTime" : parts[4] === "*" ? "dailyTime" : "custom") : "custom";
  };
  const [preset, setPreset]         = useState(initPreset);
  const [customCron, setCustomCron] = useState(scheduleInfo?.cronExpr || "0 9 * * *");
  const [timeOfDay, setTimeOfDay]   = useState(scheduleTime(scheduleInfo?.cronExpr));
  const [timezone, setTimezone]     = useState(scheduleInfo?.timezone || "UTC");
  const [enabled, setEnabled]       = useState(scheduleInfo?.enabled !== false);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [err, setErr]               = useState("");
  const [hour, minute] = timeOfDay.split(":");
  const timedCron = `${Number(minute) || 0} ${Number(hour) || 0} * * ${preset === "weekdaysTime" ? "1-5" : "*"}`;
  const cronExpr = preset === "custom" ? customCron.trim() : ["dailyTime", "weekdaysTime"].includes(preset) ? timedCron : (PRESETS.find(p => p.id === preset)?.cron || "0 9 * * *");
  const label    = parseCronHuman(cronExpr);
  const ss  = { width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 7, padding: "7px 10px", fontSize: 12, color: T.text, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
  const lbl = { fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.dim, marginBottom: 5 };

  const handleSave = async () => {
    if (!cronExpr) { setErr("Please enter a cron expression."); return; }
    setErr(""); setSaving(true);
    try {
      let res;
      if (scheduleInfo?._id) { res = await flowScheduleAPI.update(scheduleInfo._id, { cronExpr, timezone, label, enabled }); }
      else { res = await flowScheduleAPI.create({ flowDbId: flowId, cronExpr, timezone, label, enabled }); }
      onScheduleSaved(res.schedule);
    } catch (e) { setErr(e?.response?.data?.error || e.message || "Failed to save"); }
    finally { setSaving(false); }
  };
  const handleDelete = async () => {
    if (!scheduleInfo?._id) return;
    if (!window.confirm("Remove this schedule?")) return;
    setDeleting(true);
    try { await flowScheduleAPI.remove(scheduleInfo._id); onScheduleDeleted(); }
    catch (e) { setErr(e?.response?.data?.error || "Failed to delete"); }
    finally { setDeleting(false); }
  };
  const handleToggle = async () => {
    if (!scheduleInfo?._id) { setEnabled(v => !v); return; }
    const next = !enabled; setEnabled(next);
    try { const res = await flowScheduleAPI.update(scheduleInfo._id, { enabled: next }); onScheduleSaved(res.schedule); }
    catch (e) { setEnabled(!next); setErr((next ? "Failed to enable" : "Failed to pause") + ": " + (e?.response?.data?.error || e.message)); }
  };

  return (
    <div style={{ position: "absolute", top: isMobile ? "auto" : 8, right: 8, left: isMobile ? 8 : "auto", bottom: isMobile ? 8 : "auto", width: isMobile ? "calc(100vw - 16px)" : 270, background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.13)", zIndex: 600, animation: "fadeUp 0.2s ease", overflow: "hidden", maxHeight: isMobile ? "70vh" : "none" }}>
      <div style={{ padding: "12px 14px 10px", borderBottom: `1px solid ${T.border}`, background: T.accentBg, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <Clock size={14} color={T.accent} />
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Scheduler</span>
          {scheduleInfo && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: enabled ? T.greenBg : T.amberBg, color: enabled ? T.green : T.amber, border: `1px solid ${enabled ? T.greenBd : T.amberBd}` }}>{enabled ? "Active" : "Paused"}</span>}
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", padding: 2, borderRadius: 4 }}><X size={14} /></button>
      </div>
      <div style={{ padding: "12px 14px", maxHeight: 520, overflowY: "auto" }}>
        {needsSave && <div style={{ background: T.amberBg, border: `1px solid ${T.amberBd}`, borderRadius: 8, padding: "8px 10px", marginBottom: 12, display: "flex", gap: 7, alignItems: "flex-start" }}><AlertTriangle size={13} color={T.amber} style={{ flexShrink: 0, marginTop: 1 }} /><span style={{ fontSize: 11, color: "var(--lyra-color-warning)", lineHeight: 1.4 }}>Save the flow first before setting a schedule.</span></div>}
        {scheduleInfo && (
          <div style={{ background: T.greenBg, border: `1px solid ${T.greenBd}`, borderRadius: 8, padding: "8px 10px", marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.green, marginBottom: 3 }}>⏰ Current Schedule</div>
            <div style={{ fontSize: 11, fontFamily: "monospace", color: T.text }}>{scheduleInfo.cronExpr}</div>
            {scheduleInfo.label && <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>{scheduleInfo.label}</div>}
            {scheduleInfo.nextRunAt && <div style={{ fontSize: 10, color: T.muted, marginTop: 3 }}>Next: <strong>{new Date(scheduleInfo.nextRunAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</strong></div>}
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}><span style={{ fontSize: 9, color: T.muted }}>{scheduleInfo.runCount || 0} runs</span>{scheduleInfo.lastRunAt && <><span style={{ fontSize: 9, color: T.muted }}>·</span><span style={{ fontSize: 9, color: T.muted }}>Last: {new Date(scheduleInfo.lastRunAt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</span></>}</div>
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <div style={lbl}>Frequency Preset</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
            {PRESETS.map(p => <button key={p.id} onClick={() => setPreset(p.id)} disabled={needsSave} style={{ padding: "6px 8px", borderRadius: 7, fontSize: 10, fontWeight: 600, fontFamily: "inherit", cursor: needsSave ? "not-allowed" : "pointer", border: `1.5px solid ${preset === p.id ? T.accent : T.border}`, background: preset === p.id ? T.accentBg : T.white, color: preset === p.id ? T.accent : T.text, textAlign: "left", transition: "all 0.15s", opacity: needsSave ? 0.5 : 1 }}>{p.label}</button>)}
          </div>
        </div>
        {["dailyTime", "weekdaysTime"].includes(preset) && <div style={{ marginBottom: 12 }}><div style={lbl}>Time of day</div><input type="time" value={timeOfDay} onChange={e => setTimeOfDay(e.target.value)} disabled={needsSave} style={{ ...ss, opacity: needsSave ? 0.5 : 1 }} /></div>}
        {preset === "custom" && <div style={{ marginBottom: 12 }}><div style={lbl}>Custom Cron Expression</div><input value={customCron} onChange={e => setCustomCron(e.target.value)} disabled={needsSave} placeholder="e.g. 0 9 * * 1-5" style={{ ...ss, fontFamily: "monospace" }} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border} /><div style={{ fontSize: 9, color: T.dim, marginTop: 3 }}>Format: <code>minute hour day month weekday</code></div></div>}
        {cronExpr && !needsSave && <div style={{ marginBottom: 12, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 7, padding: "6px 10px", display: "flex", alignItems: "center", gap: 7 }}><Calendar size={11} color={T.muted} /><div><div style={{ fontSize: 10, color: T.muted, fontFamily: "monospace" }}>{cronExpr}</div>{label && <div style={{ fontSize: 9, color: T.dim }}>{label}</div>}</div></div>}
        <div style={{ marginBottom: 12 }}><div style={lbl}>Timezone</div><select value={timezone} onChange={e => setTimezone(e.target.value)} disabled={needsSave} style={{ ...ss, opacity: needsSave ? 0.5 : 1 }}>{TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}</select></div>
        <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>Enable schedule</span>
          <div onClick={needsSave ? undefined : handleToggle} style={{ width: 36, height: 20, borderRadius: 10, cursor: needsSave ? "not-allowed" : "pointer", position: "relative", background: enabled ? T.accent : T.border2, transition: "background 0.2s", opacity: needsSave ? 0.5 : 1 }}>
            <div style={{ position: "absolute", top: 2, left: enabled ? 16 : 2, width: 16, height: 16, borderRadius: "50%", background: T.white, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </div>
        </div>
        {err && <div style={{ background: T.redBg, border: `1px solid ${T.redBd}`, borderRadius: 7, padding: "6px 10px", fontSize: 11, color: T.red, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 7 }}>
          <button onClick={handleSave} disabled={needsSave || saving} style={{ flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: needsSave || saving ? "not-allowed" : "pointer", background: needsSave ? T.border : T.accent, color: needsSave ? T.dim : "var(--lyra-color-surface)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, opacity: needsSave ? 0.5 : 1 }}>
            {saving ? <><Loader2 size={12} style={{ animation: "spin 0.8s linear infinite" }} />Saving…</> : scheduleInfo ? "Update Schedule" : "Set Schedule"}
          </button>
          {scheduleInfo && <button onClick={handleDelete} disabled={deleting} style={{ padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: deleting ? "not-allowed" : "pointer", background: T.redBg, color: T.red, border: `1px solid ${T.redBd}`, display: "flex", alignItems: "center", gap: 5 }}>{deleting ? <Loader2 size={12} style={{ animation: "spin 0.8s linear infinite" }} /> : <Trash2 size={12} />}</button>}
        </div>
      </div>
    </div>
  );
}

// ─── Palette Tray Card (horizontal, draggable) ────────────────────────────────
function TrayCard({ item }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      draggable
      onDragStart={e => e.dataTransfer.setData("agid", item.id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flexShrink: 0,
        width: 148,
        padding: "11px 13px",
        background: hov ? item.bg : T.white,
        border: `1px solid ${hov ? item.bd : T.border}`,
        borderTop: `3px solid ${item.color}`,
        borderRadius: 9,
        cursor: "grab",
        transition: "all 0.12s",
        userSelect: "none",
        boxShadow: hov ? `0 4px 12px rgba(0,0,0,0.08)` : "none",
        transform: hov ? "translateY(-2px)" : "none",
      }}
      onMouseDown={e => { e.currentTarget.style.cursor = "grabbing"; }}
      onMouseUp={e => { e.currentTarget.style.cursor = "grab"; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
        <div style={{ color: item.color, flexShrink: 0, display: "flex", width: 18, height: 18, borderRadius: 4, overflow: "hidden" }}><AgentIcon icon={item.icon} size={18} /></div>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
      </div>
      <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.4 }}>{item.tag}</div>
      {item.isCustom && <span style={{ display: "inline-block", marginTop: 5, fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: T.accentBg, color: T.accent, border: `1px solid ${T.accentBd}` }}>MY</span>}
    </div>
  );
}

// ─── My Flows Dropdown ────────────────────────────────────────────────────────
function MyFlowsDropdown({ canvasFlows, flowId, agents, onLoadFlow, onDeleteFlow }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", zIndex: 1 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", background: "transparent", border: `1px solid ${T.border}`, borderRadius: 7, color: T.ink2, cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: "inherit" }}>
        My Flows {canvasFlows.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 8, background: T.accentBg, color: T.accent }}>{canvasFlows.length}</span>}
        <ChevronDown size={12} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 280, background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 300, overflow: "hidden", animation: "fadeUp 0.15s ease" }}>
          <div style={{ padding: "8px 12px 6px", borderBottom: `1px solid ${T.border}`, fontSize: 10, fontWeight: 700, color: T.dim, textTransform: "uppercase", letterSpacing: "0.07em" }}>Saved Flows</div>
          <div style={{ maxHeight: 320, overflowY: "auto", padding: "6px" }}>
            {!canvasFlows.length ? (
              <div style={{ textAlign: "center", padding: "24px 16px", fontSize: 12, color: T.muted }}>No saved flows yet</div>
            ) : canvasFlows.map(flow => {
              const isActive = flow._id === flowId || flow.id === flowId;
              const nodeCount = flow.canvasData?.nodes?.length || 0;
              const date = flow.updatedAt || flow.createdAt;
              const dateStr = date ? new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "";
              return (
                <div key={flow._id || flow.id}
                  style={{ background: isActive ? T.accentBg : "transparent", border: `1px solid ${isActive ? T.accentBd : "transparent"}`, borderRadius: 8, padding: "9px 10px", marginBottom: 3, cursor: "pointer", transition: "all 0.12s" }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = T.surface2; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                  onClick={() => { onLoadFlow(flow); setOpen(false); }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: isActive ? T.accent : T.text }}>{flow.name || "Untitled Flow"}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {isActive && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 6, background: T.accent, color: "var(--app-on-accent)" }}>OPEN</span>}
                      {onDeleteFlow && <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete "${flow.name}"?`)) { onDeleteFlow(flow._id || flow.id); } }}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: T.dim, display: "flex", borderRadius: 4 }}
                        onMouseEnter={e => e.currentTarget.style.color = T.red}
                        onMouseLeave={e => e.currentTarget.style.color = T.dim}><Trash2 size={11} /></button>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 5 }}>
                    {(flow.canvasData?.nodes || []).slice(0, 6).map(n => {
                      const pi = getPaletteItem(n.agid, agents);
                      return pi ? <span key={n.id} title={pi.name} style={{ width: 20, height: 20, borderRadius: 4, background: pi.bg, border: `1px solid ${pi.bd}`, display: "inline-flex", alignItems: "center", justifyContent: "center", color: pi.color, fontSize: 11, overflow: "hidden" }}><AgentIcon icon={pi.icon} size={20} /></span> : null;
                    })}
                    {nodeCount > 6 && <span style={{ fontSize: 9, fontWeight: 700, color: T.muted, alignSelf: "center" }}>+{nodeCount - 6}</span>}
                  </div>
                  <div style={{ fontSize: 10, color: T.dim }}>{nodeCount} nodes{dateStr ? ` · ${dateStr}` : ""}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FlowBuilderPage({ existingFlow = null }) {
  const { saveFlow, chains, deleteFlow, agents, loadingChains } = useAgents();
  const location = useLocation();
  const domainSelection = useDomainAgentSelection(null);
  const [connectedAgents, setConnectedAgents] = useState([]);
  useEffect(() => { externalAgentsAPI.list().then(r => setConnectedAgents((r.agents || []).filter(a => a.enabled && a.health?.status === "healthy"))).catch(() => setConnectedAgents([])); }, []);

  const dynamicPalette = (() => {
    const myAgents = (agents || []).filter(a => a.isCustom && !a.isBuiltIn && a._id);
    const myItems  = myAgents.map(a => ({ id: `custom:${a._id}`, name: a.name, tag: a.goal || a.description?.slice(0, 22) || "Custom agent", icon: a.icon || "🤖", color: a.color || T.accent, bg: T.accentBg, bd: T.accentBd, isCustom: true }));
    const aiSection = { cat: "AI Agents", items: BUILTIN_AGENT_PALETTE };
    const base = myItems.length
      ? [{ cat: "My Agents", items: myItems }, PALETTE_STATIC[0], aiSection, PALETTE_STATIC[1], PALETTE_STATIC[2]]
      : [PALETTE_STATIC[0], aiSection, PALETTE_STATIC[1], PALETTE_STATIC[2]];
    return base;
  })();

  const wrapRef = useRef(null);
  const [nodes, setNodes]       = useState([]);
  const [conns, setConns]       = useState([]);
  const [nid, setNid]           = useState(0);
  const [scale, setScale]       = useState(1);
  const [pan, setPan]           = useState({ x: 80, y: 50 });
  const [dragNode, setDragNode] = useState(null);
  const [dragOff, setDragOff]   = useState({ x: 0, y: 0 });
  const [panning, setPanning]   = useState(false);
  const [panStart, setPanStart] = useState({ sx: 0, sy: 0, px: 0, py: 0 });
  const [connecting, setConnecting] = useState(null);
  const [tmpConn, setTmpConn]   = useState(null);
  const [selNode, setSelNode]   = useState(null);
  const [ctxNode, setCtxNode]   = useState(null);
  const [schedNode, setSchedNode] = useState(null);
  const [schedules, setSchedules] = useState({});
  const [run, setRun]           = useState({ running: false, step: -1, done: new Set(), outputs: {}, connFrom: null, doneConns: new Set() });
  const [flowName, setFlowName] = useState("Untitled Flow");
  const [flowId, setFlowId]     = useState(null);
  const [saved, setSaved]       = useState(true);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [ghostPos, setGhostPos] = useState({ x: 0, y: 0 });
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugTab, setDebugTab] = useState("logs");
  const [, forceUpdate]         = useState(0);
  const [runModelId, setRunModelId] = useState(null);
  const [gmailConnected, setGmailConnected] = useState(null);
  const tgStatus = useTelegramStatus();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 900 : false));
  const { selectedModelId } = useAI();
  const initialFlowKey = useRef(null);

  // A workflow selected from the list is carried both in route state (for an
  // immediate render) and in the URL (so refreshes and shared links keep it).
  // Opening the builder directly resumes the most recently created canvas flow.
  const requestedFlowId = useMemo(
    () => new URLSearchParams(location.search).get("flow"),
    [location.search]
  );
  const selectedFlow = useMemo(() => {
    const routeFlow = location.state?.existingFlow || existingFlow;
    if (routeFlow?.canvasData) return routeFlow;

    const canvasFlows = (chains || []).filter(flow => flow?.canvasData?.nodes);
    if (requestedFlowId) {
      return canvasFlows.find(flow => String(flow._id || flow.id) === requestedFlowId) || null;
    }

    return [...canvasFlows].sort((a, b) =>
      new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0)
    )[0] || null;
  }, [chains, existingFlow, location.state, requestedFlowId]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let active = true;
    gmailAPI.status()
      .then(status => { if (active) setGmailConnected(Boolean(status?.connected)); })
      .catch(() => { if (active) setGmailConnected(false); });
    return () => { active = false; };
  }, []);

  // ── Tray state ──
  const [activeTray, setActiveTray] = useState(null); // cat string or null
  const [traySearch, setTraySearch] = useState("");

  const nodesRef = useRef(nodes); useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  const connsRef = useRef(conns); useEffect(() => { connsRef.current = conns; }, [conns]);
  const panRef   = useRef(pan);   useEffect(() => { panRef.current = pan; }, [pan]);
  const scaleRef = useRef(scale); useEffect(() => { scaleRef.current = scale; }, [scale]);

  const showToast = (msg, type = "info") => { setToast({ msg, type }); setTimeout(() => setToast(null), 2500); };

  const s2c = useCallback((sx, sy) => {
    const r = wrapRef.current?.getBoundingClientRect(); if (!r) return { x: 0, y: 0 };
    return { x: (sx - r.left - panRef.current.x) / scaleRef.current, y: (sy - r.top - panRef.current.y) / scaleRef.current };
  }, []);

  // Load the requested flow, or resume the latest one when Flow Builder is
  // opened directly. Never create a fake workflow just by visiting this page.
  useEffect(() => {
    // Wait for the flow list before deciding that direct entry has no saved flow.
    if (!selectedFlow && loadingChains) return;

    const selectedKey = selectedFlow?._id || selectedFlow?.id || "empty";
    if (initialFlowKey.current === selectedKey) return;
    initialFlowKey.current = selectedKey;

    if (selectedFlow?.canvasData?.nodes?.length) {
      const savedNodes = ensureCanvasLayout(selectedFlow.canvasData.nodes)
        .map(n => ({ ...n, item: getPaletteItem(n.agid, agents) || n.item }));
      setFlowName(selectedFlow.name || "Untitled Flow"); setFlowId(selectedFlow._id || selectedFlow.id || null);
      setNodes(savedNodes); setConns(selectedFlow.canvasData.connections || []);
      setNid(savedNodes.reduce((m, n) => Math.max(m, parseInt(n.id.replace("n","")) || 0), 0));
      setSaved(true); return;
    }
    setFlowName("Untitled Flow"); setFlowId(null); setNodes([]); setConns([]); setNid(0);
    setSaved(true); setRun({ running: false, step: -1, done: new Set(), outputs: {}, connFrom: null, doneConns: new Set() });
  }, [agents, loadingChains, selectedFlow]);

  useEffect(() => {
    if (!flowId) { setSchedules({}); return; }
    flowScheduleAPI.listForFlow(flowId).then(res => {
      const sched = (res.schedules || [])[0] || null;
      if (sched) {
        setNodes(prev => {
          const sn = prev.filter(n => n.agid === "schedule" || n.agid === "mobile-trigger");
          if (!sn.length) return prev;
          setSchedules({ [sn[0].id]: sched });
          return prev;
        });
      } else { setSchedules({}); }
    }).catch(() => {});
  }, [flowId]);

  const addNode = (agid, cx, cy) => {
    const item = getPaletteItem(agid, agents); if (!item) return;
    const id = "n"+(nid+1); setNid(n => n+1);
    // Determine default model: prefer agent's defaultModel (for custom agents), else selectedModelId from AI context
    let defaultModel = selectedModelId || '';
    if (agid.startsWith('custom:')) {
      const aid = agid.replace('custom:', '');
      const found = agents.find(a => (a._id || a.id) === aid);
      if (found && (found.defaultModel || found.model)) defaultModel = found.defaultModel || found.model;
    }
    setNodes(p => [...p, { id, agid, item, brief: "", x: Math.round(cx/20)*20, y: Math.round(cy/20)*20, model: defaultModel || undefined, ...(agid === "condition" ? { ...DEFAULT_CONDITION } : {}) }]);
    setSaved(false);
  };
  const removeNode = (id) => {
    setNodes(p => p.filter(n => n.id !== id)); setConns(p => p.filter(c => c.from !== id && c.to !== id));
    if (selNode === id) setSelNode(null); if (ctxNode?.id === id) setCtxNode(null); if (schedNode?.id === id) setSchedNode(null);
    setSaved(false);
  };
  const deleteConn = (idx) => { setConns(p => p.filter((_, i) => i !== idx)); setSaved(false); };

  const loadFlow = (flow) => {
    if (!flow?.canvasData?.nodes?.length) { showToast("This flow has no canvas data", "warn"); return; }
    if (nodes.length && !window.confirm("Replace current canvas with this flow?")) return;
    const savedNodes = ensureCanvasLayout(flow.canvasData.nodes)
      .map(n => ({ ...n, item: getPaletteItem(n.agid, agents) || n.item }));
    setFlowName(flow.name || "Untitled Flow"); setFlowId(flow._id || flow.id || null);
    setNodes(savedNodes); setTimeout(() => setConns(flow.canvasData.connections || []), 80);
    setNid(savedNodes.reduce((m, n) => Math.max(m, parseInt(n.id.replace("n","")) || 0), 0));
    setRun({ running: false, step: -1, done: new Set(), outputs: {}, connFrom: null, doneConns: new Set() });
    setSelNode(null); setCtxNode(null); setSchedNode(null); setSchedules({}); setSaved(true);
    showToast(`Loaded "${flow.name}"`, "success");
  };

  const onNodeMouseDown = (e, id) => {
    if (e.button !== 0) return;
    const t = e.target;
    if (t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.tagName === "INPUT" || t.closest("button")) return;
    e.stopPropagation();
    setSelNode(id); setDragNode(id);
    const n = nodesRef.current.find(x => x.id === id);
    const c = s2c(e.clientX, e.clientY);
    setDragOff({ x: c.x - n.x, y: c.y - n.y });
  };
  const onWrapDown = (e) => {
    if (!dragNode && !connecting) { setPanning(true); setPanStart({ sx: e.clientX, sy: e.clientY, px: panRef.current.x, py: panRef.current.y }); }
    if (!e.target.closest("[id^='fn-']")) { setSelNode(null); setCtxNode(null); setSchedNode(null); }
  };
  const onWrapMove = (e) => {
    const c = s2c(e.clientX, e.clientY);
    if (dragNode) { setNodes(p => p.map(n => n.id === dragNode ? { ...n, x: Math.round((c.x-dragOff.x)/20)*20, y: Math.round((c.y-dragOff.y)/20)*20 } : n)); }
    else if (panning) { setPan({ x: panStart.px + (e.clientX-panStart.sx), y: panStart.py + (e.clientY-panStart.sy) }); }
    else if (connecting) { setTmpConn({ ...connecting, ex: c.x, ey: c.y }); }
  };
  const onWrapUp = (e) => {
    setDragNode(null); setPanning(false);
    if (connecting) {
      const c = s2c(e.clientX, e.clientY);
      const hit = nodesRef.current.find(n => {
        if (n.id === connecting.from) return false;
        const el = document.getElementById("fn-"+n.id); if (!el) return false;
        return c.x >= n.x && c.x <= n.x+el.offsetWidth && c.y >= n.y && c.y <= n.y+el.offsetHeight;
      });
      if (hit && !connsRef.current.find(c2 => c2.from === connecting.from && c2.to === hit.id)) {
        const lbls = { out: "Output", true: "If true", false: "If false" };
        setConns(p => [...p, { from: connecting.from, to: hit.id, port: connecting.port, label: lbls[connecting.port] || "Output" }]);
        setSaved(false);
      }
      setConnecting(null); setTmpConn(null);
    }
  };
  const onWrapWheel = (e) => {
    e.preventDefault();
    const d = -e.deltaY * 0.001, ns = Math.max(0.2, Math.min(2.5, scaleRef.current + d));
    const r = wrapRef.current?.getBoundingClientRect(); if (!r) return;
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    setPan(p => ({ x: mx - (mx-p.x)*(ns/scaleRef.current), y: my - (my-p.y)*(ns/scaleRef.current) }));
    setScale(ns);
  };
  const onDrop = (e) => { e.preventDefault(); const agid = e.dataTransfer.getData("agid"); if (!agid) return; const c = s2c(e.clientX, e.clientY); addNode(agid, c.x, c.y); };

  const topoSort = () => {
    const deg = {}, adj = {};
    nodes.forEach(n => { deg[n.id] = 0; adj[n.id] = []; });
    conns.forEach(c => { if (deg[c.to] !== undefined) deg[c.to]++; if (adj[c.from]) adj[c.from].push(c.to); });
    const q = nodes.filter(n => deg[n.id] === 0), res = [], vis = new Set();
    while (q.length) { const n = q.shift(); if (vis.has(n.id)) continue; vis.add(n.id); res.push(n); (adj[n.id]||[]).forEach(tid => { const t = nodes.find(x => x.id === tid); if (t && !vis.has(t.id)) q.push(t); }); }
    nodes.forEach(n => { if (!vis.has(n.id)) res.push(n); }); return res;
  };

  const runFlow = async () => {
    if (!nodes.length) { showToast("Add some agents first!", "warn"); return; }
    const incompleteGmail = nodes.find(n => n.agid === "gmail" && !String(n.toEmail || "").trim());
    if (incompleteGmail) { setSelNode(incompleteGmail.id); setCtxNode(incompleteGmail); showToast("Add a recipient email to the Gmail Send node before running.", "warn"); return; }
    const order = topoSort();
    setRun({ running: true, step: 0, done: new Set(), outputs: {}, connFrom: null, doneConns: new Set() });

    if (flowId) {
      try {
        let i = 0;
        const animTimer = setInterval(() => {
          if (i < order.length && order[i]) { const step = i; const nodeId = order[i].id; setRun(r => ({ ...r, step, connFrom: nodeId })); i++; }
          else clearInterval(animTimer);
        }, 1200);
        const effectiveModel = runModelId || selectedModelId || undefined;
        const data = await customAgentAPI.runFlow(
          flowId,
          nodes.map(n => ({ id: n.id, agid: n.agid, brief: n.brief || "", toEmail: n.toEmail || "", triggerSubject: n.triggerSubject || "", triggerEnabled: !!n.triggerEnabled, item: { name: n.item?.name }, conditionMode: n.conditionMode, conditionExpr: n.conditionExpr, conditionRule: n.conditionRule })),
          conns,
          "Manual test run — no future event is being awaited.",
          flowName,
          effectiveModel
        );
        clearInterval(animTimer);
        const nodeResults = data.nodeResults || [];
        nodeResults.forEach((r, idx) => {
          setTimeout(() => {
            setRun(prev => {
              const nd = new Set(prev.done); nd.add(r.nodeId);
              const dc = new Set(prev.doneConns); dc.add(r.nodeId);
              const isLast = idx === nodeResults.length - 1;
              return { ...prev, step: idx, connFrom: isLast ? null : r.nodeId, done: nd, doneConns: dc, outputs: { ...prev.outputs, [r.nodeId]: r.output }, ...(isLast ? { running: false, step: -1 } : {}) };
            });
            if (idx === nodeResults.length - 1) { const hasError = nodeResults.some(r => r.error); showToast(hasError ? "⚠️ Flow completed with errors" : "✅ Flow completed!", hasError ? "warn" : "success"); }
          }, idx * 500);
        });
      } catch (err) {
        setRun({ running: false, step: -1, done: new Set(), outputs: {}, connFrom: null, doneConns: new Set() });
        showToast(`❌ Run failed: ${err?.response?.data?.error || err.message}`, "warn");
      }
      return;
    }

    // Demo mode
    const evalExprDemo = (expr, prevOut) => {
      const { field, operator, value, customField } = expr || {};
      const resolvePath = (obj, path) => { try { return (path||"").replace(/\[(\d+)\]/g,".$1").split(".").reduce((a,k) => a?.[k], obj); } catch { return undefined; } };
      let fieldVal;
      if (field === "output") { fieldVal = typeof prevOut === "object" ? JSON.stringify(prevOut) : String(prevOut ?? ""); }
      else if (field === "custom") { let parsed = prevOut; if (typeof prevOut === "string") { try { parsed = JSON.parse(prevOut); } catch {} } fieldVal = resolvePath(parsed, customField || ""); }
      else { let parsed = prevOut; if (typeof prevOut === "string") { try { parsed = JSON.parse(prevOut); } catch {} } const subPath = (field||"").startsWith("output.") ? field.slice(7) : (field||""); fieldVal = resolvePath(parsed, subPath); }
      const numOps = ["gt","lt","gte","lte"];
      if (numOps.includes(operator)) { const nf = parseFloat(fieldVal), nv = parseFloat(value); if (isNaN(nf)||isNaN(nv)) return false; if (operator==="gt") return nf>nv; if (operator==="lt") return nf<nv; if (operator==="gte") return nf>=nv; if (operator==="lte") return nf<=nv; }
      const sf = String(fieldVal??"").toLowerCase(), sv = String(value??"").toLowerCase();
      switch (operator) { case "contains": return sf.includes(sv); case "not_contains": return !sf.includes(sv); case "equals": return sf===sv; case "not_equals": return sf!==sv; case "starts_with": return sf.startsWith(sv); case "ends_with": return sf.endsWith(sv); case "is_truthy": return !!fieldVal && fieldVal!=="false" && fieldVal!=="0" && fieldVal!==""; case "is_empty": return !fieldVal||fieldVal===""||fieldVal==="null"||fieldVal==="undefined"; default: return false; }
    };
    const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
    const adjMap = {}; nodes.forEach(n => { adjMap[n.id] = []; }); conns.forEach(c => { if (adjMap[c.from]) adjMap[c.from].push({ toId: c.to, port: c.port }); });
    const hasIncoming = new Set(conns.map(c => c.to));
    const roots = nodes.filter(n => !hasIncoming.has(n.id));
    const bfsQueue = roots.map(n => n.id), visited = new Set(), walkOrder = [], skipped = new Set(), condResults = {}, demoOutputs = {};
    while (bfsQueue.length) {
      const nid2 = bfsQueue.shift(); if (visited.has(nid2)||skipped.has(nid2)) continue; visited.add(nid2);
      const node = nodeMap[nid2]; if (!node) continue; walkOrder.push(node);
      if (node.agid === "condition") {
        const incoming = conns.find(c => c.to === nid2); const prevOut = incoming ? (demoOutputs[incoming.from] ?? "") : "";
        const result = (node.conditionMode||"expression") === "ai" ? true : evalExprDemo(node.conditionExpr, prevOut);
        condResults[nid2] = result;
        const activeBranch = result ? "true" : "false", inactiveBranch = result ? "false" : "true";
        const skipSubtree = (id) => { if (visited.has(id)||skipped.has(id)) return; skipped.add(id); (adjMap[id]||[]).forEach(e => skipSubtree(e.toId)); };
        (adjMap[nid2]||[]).filter(e => e.port === inactiveBranch).forEach(e => skipSubtree(e.toId));
        (adjMap[nid2]||[]).filter(e => e.port === activeBranch).forEach(e => { if (!visited.has(e.toId)&&!skipped.has(e.toId)) bfsQueue.push(e.toId); });
        demoOutputs[nid2] = getConditionDemoOutput(node);
      } else {
        demoOutputs[nid2] = SAMPLE_OUT[node.agid] || "✅ Done.";
        (adjMap[nid2]||[]).forEach(e => { if (!visited.has(e.toId)&&!skipped.has(e.toId)) bfsQueue.push(e.toId); });
      }
    }
    walkOrder.forEach((node, i) => {
      const isLast = i === walkOrder.length - 1;
      setTimeout(() => {
        setRun(r => ({ ...r, connFrom: node.id }));
        setTimeout(() => {
          const out = node.agid === "condition"
            ? getConditionDemoOutput(node).replace(/→ ✅ TRUE \(demo\)|→ ❌ FALSE \(demo\)|→ ✅ TRUE|→ ❌ FALSE/, condResults[node.id] ? "→ ✅ TRUE" : "→ ❌ FALSE")
            : demoOutputs[node.id] || "✅ Done.";
          setRun(r => {
            const nd = new Set(r.done); nd.add(node.id);
            const dc = new Set(r.doneConns); dc.add(node.id);
            return { ...r, connFrom: null, done: nd, doneConns: dc, outputs: { ...r.outputs, [node.id]: out }, ...(isLast ? { running: false, step: -1 } : {}) };
          });
          if (isLast) showToast("✅ Demo run complete — save flow for real execution", "info");
        }, 1300);
      }, i * 1750);
    });
  };

  const autoLayout = () => {
    const deg = {}, adj = {};
    nodes.forEach(n => { deg[n.id] = 0; adj[n.id] = []; });
    conns.forEach(c => { if (deg[c.to] !== undefined) deg[c.to]++; if (adj[c.from]) adj[c.from].push(c.to); });
    const levels = {}, vis = new Set(); let queue = nodes.filter(n => deg[n.id] === 0), lv = 0;
    while (queue.length) { queue.forEach(n => { if (!vis.has(n.id)) { vis.add(n.id); levels[n.id] = lv; } }); const nx = []; queue.forEach(n => (adj[n.id]||[]).forEach(tid => { const t = nodes.find(x => x.id === tid); if (t && !vis.has(t.id)) nx.push(t); })); queue = nx; lv++; }
    nodes.forEach(n => { if (levels[n.id] === undefined) levels[n.id] = lv++; });
    const cols = {};
    setNodes(p => p.map(n => { const c = levels[n.id], r = cols[c]||0; cols[c] = r+1; return { ...n, x: 80+c*270, y: 80+r*220 }; }));
    setScale(1); setPan({ x: 80, y: 50 });
  };

  const doSaveFlow = async () => {
    if (!nodes.length) { showToast("Add some agents first!", "warn"); return; }
    setSaving(true);
    try {
      const { chain, isNew } = await saveFlow(flowName, nodes, conns, flowId);
      const newId = chain._id || chain.id;
      if (isNew) setFlowId(newId);
      setSaved(true);
      if (schedNode && !flowId) showToast("Flow saved! You can now set a schedule.", "info");
    } catch { }
    finally { setSaving(false); }
  };

  const order = topoSort();
  const canvasFlows = (chains || []).filter(c => c.source === "canvas" && c.canvasData?.nodes?.length);

  // ── Tray items for active category ──
  const activeCatData = activeTray ? dynamicPalette.find(c => c.cat === activeTray) : null;
  const trayItems = activeCatData
    ? activeCatData.items.filter(i => !traySearch || i.name.toLowerCase().includes(traySearch.toLowerCase()) || i.tag.toLowerCase().includes(traySearch.toLowerCase()))
    : [];

  // Category tab accent colors
  const CAT_ACCENT = {
    "Triggers":  T.amber,
    "My Agents": T.accent,
    "AI Agents": "var(--lyra-color-primary)",
    "Logic":     T.violet,
    "Outputs":   T.red,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "100vh", overflow: "hidden", background: T.bg, fontFamily: "'DM Sans',system-ui,-apple-system,sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;font-family:'DM Sans',sans-serif;}
        input[type=range]{cursor:pointer;}
        ::-webkit-scrollbar{width:4px;height:4px;}
        ::-webkit-scrollbar-thumb{background:var(--lyra-color-border);border-radius:4px;}
        @keyframes spin{to{transform:rotate(360deg);}}
        @keyframes fadeUp  { from { opacity:0; transform:translateY(5px)  } to { opacity:1; transform:translateY(0) } }
        @keyframes slideIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }
        @keyframes trayIn  { from { opacity:0; transform:translateY(-8px) } to { opacity:1; transform:translateY(0) } }
        textarea::placeholder, input::placeholder { color:${T.dim} }
      `}</style>

      {/* ── TOPBAR ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, padding: isMobile ? "10px 12px" : "0 20px", minHeight: 52, background: T.white, borderBottom: `1px solid ${T.border}`, flexShrink: 0, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <GitBranch size={16} style={{ color: T.accent }} />
            <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>Flow Builder</span>
          </div>
          {!isMobile && <span style={{ color: T.border2 }}>/</span>}
          <input value={flowName} onChange={e => { setFlowName(e.target.value); setSaved(false); }}
            style={{ border: "none", background: "transparent", outline: "none", fontSize: 13, color: T.muted, fontFamily: "inherit", width: isMobile ? "100%" : 160, minWidth: isMobile ? 180 : "auto" }} />
          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: saved ? T.greenBg : T.amberBg, color: saved ? T.green : T.amber, border: `1px solid ${saved ? T.greenBd : T.amberBd}` }}>
            {saved ? "● Saved" : "● Unsaved"}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", width: isMobile ? "100%" : "auto" }}>
          <span style={{ fontSize: 12, color: T.dim, width: isMobile ? "100%" : "auto" }}>{nodes.length} nodes · {conns.length} connections</span>

          {/* My Flows dropdown */}
          <MyFlowsDropdown canvasFlows={canvasFlows} flowId={flowId} agents={agents} onLoadFlow={loadFlow} onDeleteFlow={deleteFlow} />

          <button onClick={() => { if (window.confirm("Clear all?")) { setNodes([]); setConns([]); setSelNode(null); setCtxNode(null); setRun({ running: false, step: -1, done: new Set(), outputs: {}, connFrom: null, doneConns: new Set() }); } }}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", background: "transparent", border: `1px solid ${T.border}`, borderRadius: 7, color: T.muted, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", flex: isMobile ? "1 1 calc(50% - 6px)" : "0 0 auto", justifyContent: "center" }}>
            <Trash2 size={11} />Clear
          </button>

          {/* Undo / Redo */}
          {!isMobile && [{
            title: "Undo", icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"/><path d="M3 13A9 9 0 1 0 6 5.3"/></svg>
          }, {
            title: "Redo", icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6"/><path d="M21 13A9 9 0 1 1 18 5.3"/></svg>
          }].map(b => (
            <button key={b.title} onClick={() => {}} title={b.title} style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: `1px solid ${T.border}`, borderRadius: 7, color: T.muted, cursor: "pointer" }}>{b.icon}</button>
          ))}
          {!isMobile && <div style={{ width: 1, height: 20, background: T.border, margin: "0 2px" }} />}
          <button onClick={autoLayout} title="Auto Layout" style={{ width: isMobile ? "auto" : 30, minWidth: isMobile ? "calc(50% - 6px)" : "auto", height: 30, display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 5 : 0, background: "transparent", border: `1px solid ${T.border}`, borderRadius: 7, color: T.muted, cursor: "pointer", padding: isMobile ? "0 10px" : 0, flex: isMobile ? "1 1 calc(50% - 6px)" : "0 0 auto" }}>{isMobile && "Auto Layout"}<LayoutGrid size={13} /></button>
          <button onClick={doSaveFlow} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", background: "transparent", border: `1px solid ${T.border2}`, borderRadius: 7, color: T.ink, cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: "inherit", flex: isMobile ? "1 1 calc(50% - 6px)" : "0 0 auto", justifyContent: "center" }}>
            {saving ? <Loader2 size={11} style={{ animation: "spin 0.8s linear infinite" }} /> : <Save size={11} />}Save Flow
          </button>
          <div style={{ flex: isMobile ? "1 1 100%" : "0 0 auto" }}>
            <ModelPickerInline onChangeModel={setRunModelId} />
          </div>
          <button onClick={runFlow} disabled={run.running || !nodes.length}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "6px 16px", borderRadius: 7, cursor: run.running||!nodes.length ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 500, fontFamily: "inherit", border: "none", background: run.running ? T.amberBg : nodes.length ? T.accent : T.bg, color: run.running ? T.amber : nodes.length ? "var(--lyra-color-surface)" : T.dim, outline: `1px solid ${run.running ? T.amberBd : nodes.length ? "transparent" : T.border}`, flex: isMobile ? "1 1 100%" : "0 0 auto" }}>
            {run.running ? <><Loader2 size={11} style={{ animation: "spin 0.8s linear infinite" }} />Testing...</> : <><Play size={11} fill="currentColor" />Test flow</>}
          </button>
        </div>
      </div>

      {/* ── CATEGORY TOOLBAR ── */}
      <div style={{ display: "flex", alignItems: "stretch", background: T.white, borderBottom: `1px solid ${T.border}`, flexShrink: 0, padding: isMobile ? "0 8px" : "0 20px", gap: 0, zIndex: 0, overflowX: "auto" }}>
        {dynamicPalette.map(cat => {
          const accent = CAT_ACCENT[cat.cat] || T.accent;
          const isActive = activeTray === cat.cat;
          return (
            <button key={cat.cat}
              onClick={() => { setActiveTray(isActive ? null : cat.cat); setTraySearch(""); }}
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 16px", background: "none", border: "none", borderBottom: `2px solid ${isActive ? accent : "transparent"}`, color: isActive ? accent : T.muted, cursor: "pointer", fontSize: 13, fontWeight: isActive ? 700 : 500, fontFamily: "inherit", transition: "all 0.12s", whiteSpace: "nowrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: isActive ? alpha(accent, '18') : T.surface2, color: isActive ? accent : T.dim, transition: "all 0.12s" }}>{cat.items.length}</span>
              {cat.cat}
              {isActive ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          );
        })}
        {/* Filter search — only visible when tray open */}
        {activeTray && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", padding: "6px 0", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 7, background: T.surface2, border: `1px solid ${T.border}` }}>
              <Search size={12} color={T.dim} />
              <input value={traySearch} onChange={e => setTraySearch(e.target.value)} placeholder={`Filter ${activeTray}…`} style={{ border: "none", background: "transparent", outline: "none", fontSize: 12, color: T.text, width: isMobile ? 120 : 140, fontFamily: "inherit" }} autoFocus />
              {traySearch && <button onClick={() => setTraySearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: T.dim, display: "flex" }}><X size={10} /></button>}
            </div>
          </div>
        )}
      </div>

      {/* ── SLIDE-DOWN TRAY ── */}
      {activeTray && (
        <div style={{ background: T.white, borderBottom: `1px solid ${T.border}`, flexShrink: 0, padding: isMobile ? "10px 12px" : "10px 20px", animation: "trayIn 0.15s ease", zIndex: 0 }}>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
            {trayItems.length === 0
              ? <div style={{ fontSize: 12, color: T.dim, padding: "8px 0" }}>No items match your filter.</div>
              : trayItems.map(item => <TrayCard key={item.id} item={item} />)
            }
          </div>
          <div style={{ marginTop: 8, fontSize: 10.5, color: T.dim }}>
            ✦ Drag any card onto the canvas below to add it to your flow
          </div>
        </div>
      )}

      {/* ── CANVAS AREA ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          <div ref={wrapRef}
            style={{ position: "absolute", inset: 0, overflow: "hidden", cursor: panning ? "grabbing" : connecting ? "crosshair" : "default", border: isDragOver ? `2px dashed ${T.accentMid}` : "2px solid transparent", background: isDragOver ? "rgba(168,206,202,0.06)" : "transparent", transition: "border-color 0.15s, background 0.15s" }}
            onMouseDown={onWrapDown} onMouseMove={onWrapMove} onMouseUp={onWrapUp}
            onWheel={onWrapWheel}
            onDragOver={e => { e.preventDefault(); setIsDragOver(true); setGhostPos({ x: e.clientX, y: e.clientY }); }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false); }}
            onDrop={e => { setIsDragOver(false); onDrop(e); }}>
            {/* Dot grid */}
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: `radial-gradient(circle,${T.border} 1px,transparent 1px)`, backgroundSize: "28px 28px" }} />
            {/* Drop ghost */}
            {isDragOver && <div style={{ position: "fixed", left: ghostPos.x-80, top: ghostPos.y-34, width: 160, height: 68, background: T.accentLight, border: `1.5px dashed ${T.accent}`, borderRadius: 10, opacity: 0.85, pointerEvents: "none", zIndex: 9999 }} />}
            {/* Canvas root */}
            <div style={{ position: "absolute", top: 0, left: 0, width: 4000, height: 3000, transformOrigin: "0 0", transform: `translate(${pan.x}px,${pan.y}px) scale(${scale})` }}>
              <ConnectorLayer nodes={nodes} connections={conns} runningFrom={run.connFrom} doneConns={run.doneConns} onDeleteConn={deleteConn} tmpConn={tmpConn} />
              {nodes.map(node => (
                <FlowNode key={node.id} node={node}
                  isSelected={selNode === node.id}
                  isRunning={run.running && run.step >= 0 && order[run.step]?.id === node.id}
                  isDone={run.done.has(node.id)}
                  output={run.outputs[node.id]}
                  scheduleInfo={schedules[node.id] || null}
                  onMouseDown={e => onNodeMouseDown(e, node.id)}
                  onDoubleClick={e => { e.stopPropagation(); if (node.agid === "schedule") { setSchedNode(node); setCtxNode(null); } else if (node.agid === "condition" || node.agid === "mobile-trigger") { /* configured inline */ } else { setCtxNode(node); setSchedNode(null); } }}
                  onRemove={() => removeNode(node.id)}
                  onConnectStart={(e, nid2, port) => { e.stopPropagation(); setConnecting({ from: nid2, port }); }}
                  onConditionChange={() => { setSaved(false); forceUpdate(n => n+1); }}
                  onTriggerChange={(triggerChannel, triggerEnabled) => {
                    setNodes(prev => prev.map(n => n.id === node.id ? { ...n, triggerChannel, triggerEnabled } : n));
                    setSaved(false);
                  }}
                  onTriggerConfigure={updates => {
                    setNodes(prev => prev.map(n => n.id === node.id ? { ...n, ...updates } : n));
                    setSaved(false);
                  }}
                  onScheduleSetup={() => { setSchedNode(node); setCtxNode(null); }}
                  gmailConnected={gmailConnected}
                  telegramConnected={tgStatus.connected}
                  flowSaved={saved}
                  agentOptions={[
                    ...BUILTIN_AGENT_PALETTE.map(a => ({ id:a.id, name:a.name, group:"Lyra" })),
                    ...domainSelection.entries.map(entry => ({ id:`domain:${entry.agent.id}`, name:entry.agent.name || entry.agent.id, group:`Domain · ${entry.label}` })),
                    ...connectedAgents.map(a => ({ id:`external:${a._id}`, name:a.name, group:"Connected" })),
                    ...(agents || []).filter(a => a._id || a.id).map(a => ({ id:`custom:${a._id || a.id}`, name:a.name || "Untitled agent", group:a.provider === "mcp" || a.external ? "External" : "Custom" }))
                  ]}
                  onAgentChange={agid => {
                    const choice = [
                      ...BUILTIN_AGENT_PALETTE.map(a => ({ id:a.id, name:a.name, group:"Lyra" })),
                      ...domainSelection.entries.map(entry => ({ id:`domain:${entry.agent.id}`, name:entry.agent.name || entry.agent.id, group:`Domain · ${entry.label}` })),
                      ...connectedAgents.map(a => ({ id:`external:${a._id}`, name:a.name, group:"Connected" })),
                      ...(agents || []).filter(a => a._id || a.id).map(a => ({ id:`custom:${a._id || a.id}`, name:a.name || "Untitled agent", group:"Custom" })),
                    ].find(option => option.id === agid);
                    const item = getPaletteItem(agid, agents) || (choice ? { ...node.item, name:choice.name, tag:choice.group, icon:<Bot size={15}/>, color:T.green, bg:T.greenBg, bd:T.greenBd } : null);
                    if (item) { setNodes(p => p.map(n => n.id === node.id ? { ...n, agid, item } : n)); setSaved(false); }
                  }}
                />
              ))}
            </div>
          </div>

          {/* Empty state */}
          {nodes.length === 0 && (
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", border: `2px dashed ${T.border2}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 14px" }}>🧩</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.muted, marginBottom: 5 }}>Select a category above to add nodes</div>
              <div style={{ fontSize: 12, color: T.dim }}>Click a tab, then drag cards onto the canvas</div>
            </div>
          )}

          {/* Node config panel */}
          {ctxNode && ctxNode.agid !== "schedule" && ctxNode.agid !== "condition" && (
            <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, pointerEvents: "none" }}>
              <div style={{ pointerEvents: "auto" }}><NodeConfigPanel key={ctxNode.id} node={ctxNode} onClose={() => { setSaved(false); setCtxNode(null); }} isMobile={isMobile} /></div>
            </div>
          )}

          {/* Scheduler panel */}
          {schedNode && (
            <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, pointerEvents: "none" }}>
              <div style={{ pointerEvents: "auto" }}>
                <SchedulerConfigPanel node={schedNode} flowId={flowId} scheduleInfo={schedules[schedNode.id] || null}
                  onScheduleSaved={sched => { setSchedules(prev => ({ ...prev, [schedNode.id]: sched })); showToast("Schedule saved!", "success"); }}
                  onScheduleDeleted={() => { setSchedules(prev => { const n = { ...prev }; delete n[schedNode.id]; return n; }); showToast("Schedule removed", "info"); }}
                  onClose={() => setSchedNode(null)} isMobile={isMobile} />
              </div>
            </div>
          )}

          {/* Zoom controls */}
          <div style={{ position: "absolute", bottom: run.running ? 60 : 16, left: 16, display: "flex", alignItems: "center", gap: 6, zIndex: 100, transition: "bottom 0.3s" }}>
            {[{ l: "−", d: -0.15 }, { l: "+", d: 0.15 }].map(b => (
              <button key={b.l} onClick={() => setScale(s => Math.max(0.2, Math.min(2.5, s+b.d)))}
                style={{ width: 30, height: 30, borderRadius: 7, background: T.white, border: `1px solid ${T.border}`, color: T.muted, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", fontFamily: "inherit" }}>{b.l}</button>
            ))}
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 600, background: T.white, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 9px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>{Math.round(scale*100)}%</span>
            <button onClick={() => { setScale(1); setPan({ x: 80, y: 50 }); }}
              style={{ width: 30, height: 30, borderRadius: 7, background: T.white, border: `1px solid ${T.border}`, color: T.muted, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>⌖</button>
          </div>

          {/* Run bar */}
          {run.running && (
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, minHeight: 48, background: T.white, borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", padding: isMobile ? "8px 12px" : "0 20px", gap: 10, zIndex: 100, animation: "slideIn 0.3s ease", flexShrink: 0, overflowX: "auto" }}>
              <Loader2 size={12} color={T.amber} style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.amber, flexShrink: 0 }}>Running...</span>
              <div style={{ display: "flex", alignItems: "center", gap: 5, overflowX: "auto" }}>
                {order.map((n, i) => (
                  <span key={n.id} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 600, transition: "all 0.3s", whiteSpace: "nowrap", background: run.done.has(n.id) ? T.greenBg : run.step === i ? T.amberBg : T.bg, color: run.done.has(n.id) ? T.green : run.step === i ? T.amber : T.dim, border: `1px solid ${run.done.has(n.id) ? T.greenBd : run.step === i ? T.amberBd : T.border}` }}>
                      <span style={{ display: "inline-flex", alignItems: "center", width: 14, height: 14, borderRadius: 3, overflow: "hidden", flexShrink: 0 }}><AgentIcon icon={n.item?.icon} size={14} /></span> {n.item?.name}
                    </span>
                    {i < order.length - 1 && <span style={{ color: T.dim, fontSize: 11 }}>→</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── DEBUG PANEL ── */}
        <div style={{ flexShrink: 0, height: debugOpen ? 220 : 32, background: T.white, borderTop: `1px solid ${T.border}`, transition: "height 0.2s ease", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div onClick={() => setDebugOpen(o => !o)} style={{ height: 32, display: "flex", alignItems: "center", gap: 10, padding: "0 14px", cursor: "pointer", flexShrink: 0, userSelect: "none" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.muted }}>Debug</span>
            <div style={{ display: "flex", gap: 2 }}>
              {["Logs","Outputs","Errors"].map(tab => (
                <button key={tab} onClick={e => { e.stopPropagation(); setDebugTab(tab.toLowerCase()); if (!debugOpen) setDebugOpen(true); }}
                  style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, border: `1px solid ${debugTab === tab.toLowerCase() && debugOpen ? T.accentMid : T.border}`, background: debugTab === tab.toLowerCase() && debugOpen ? T.accentLight : "transparent", color: debugTab === tab.toLowerCase() && debugOpen ? T.accent : T.muted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                  {tab}
                </button>
              ))}
            </div>
            <span style={{ marginLeft: "auto", fontSize: 11, color: T.dim, transform: debugOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>▲</span>
          </div>
          {debugOpen && (
            <div style={{ flex: 1, padding: "8px 14px", overflowY: "auto", borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 11, color: T.dim }}>&nbsp;</div>
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 64, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: toast.type === "success" ? T.greenBg : toast.type === "warn" ? T.amberBg : T.white, border: `1px solid ${toast.type === "success" ? T.greenBd : toast.type === "warn" ? T.amberBd : T.border}`, borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 600, color: toast.type === "success" ? T.green : toast.type === "warn" ? T.amber : T.text, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", animation: "slideIn 0.2s ease" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
